import React, { useCallback, useEffect, useState } from 'react';
import { flushSync } from 'react-dom';
import styled from 'styled-components';
import { Alarm, AlarmSet, AssignmentCategory, Class, User } from '../types/alarm';
import TimeTagSettingsModal, { TimeTag } from './TimeTagSettingsModal';
import { assignmentCategoryService } from '../services/assignmentCategoryService';
import { boardService, NotebookCategory } from '../services/boardService';
import { defaultSetService } from '../services/defaultSetService';
import { userDataService } from '../services/userDataService';
import { PointTypeManagementService } from '../services/pointTypeManagementService';
import { ItemCardService } from '../services/itemCardService';
import { TimeTagService } from '../services/timeTagService';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { db, auth } from '../firebase/config';
import ConfirmModal from './ConfirmModal';
import LicenseLimitModal from './LicenseLimitModal';
import { checkNoteCategoryLimit } from '../utils/licenseLimits';
import { getLicenseLimits } from '../config/licenseLimits';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

/** 노트북 잠금 비밀번호를 SHA-256 해시로 변환 (저장용) */
async function hashLockPassword(password: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(password));
  return Array.from(new Uint8Array(buf))
    .map(b => b.toString(16).padStart(2, '0'))
    .join('');
}

// 기본 색상 팔레트 (빨강, 주황, 노랑, 연두, 초록, 청록, 하늘, 파랑, 보라, 자주)
const DEFAULT_COLORS = [
  '#E74C3C', // 빨강
  '#E67E22', // 주황
  '#F1C40F', // 노랑
  '#7DCEA0', // 연두
  '#27AE60', // 초록
  '#1ABC9C', // 청록
  '#3498DB', // 하늘
  '#2980B9', // 파랑
  '#9B59B6', // 보라
  '#8E44AD'  // 자주
];

// 드래그 가능한 과제 카테고리 행
type SortableAssignmentCategoryRowProps = {
  cat: AssignmentCategory;
  colorPickerOpen: string | null;
  popupPosition: { x: number; y: number };
  renameCategory: (id: string, name: string) => void;
  openColorPicker: (id: string, e: React.MouseEvent<HTMLElement>) => void;
  updateCategoryColor: (id: string, color: string) => void;
  deleteCategory: (id: string) => void;
};

const SortableAssignmentCategoryRow: React.FC<SortableAssignmentCategoryRowProps> = ({
  cat,
  colorPickerOpen,
  popupPosition,
  renameCategory,
  openColorPicker,
  updateCategoryColor,
  deleteCategory,
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cat.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <Item ref={setNodeRef} style={style}>
      <Input
        value={cat.name}
        onChange={e => renameCategory(cat.id, e.target.value)}
      />
      <ColorSelector>
        <ColorDisplay
          $color={cat.color}
          onClick={(e) => openColorPicker(cat.id, e)}
          title="색상 변경"
        />
        {colorPickerOpen === cat.id && (
          <ColorPickerPopup style={{ left: popupPosition.x, top: popupPosition.y }}>
            {DEFAULT_COLORS.map(color => (
              <ColorOption
                key={color}
                $color={color}
                $isSelected={cat.color === color}
                onClick={() => updateCategoryColor(cat.id, color)}
                title={color}
              />
            ))}
          </ColorPickerPopup>
        )}
      </ColorSelector>
      <DangerButton onClick={() => deleteCategory(cat.id)} title="삭제">
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </DangerButton>
      <DragHandle {...attributes} {...listeners} aria-label="카테고리 순서 조정" />
    </Item>
  );
};

// 드래그 가능한 노트북 카테고리 행 (모달 외부 정의, 동일 UI 유지)
type SortableNoteCategoryRowProps = {
  cat: NotebookCategory;
  renameNoteCategory: (id: string, name: string) => void;
  deleteNoteCategory: (id: string) => void;
  toggleLockNoteCategory?: (id: string) => void;
};

const SortableNoteCategoryRow: React.FC<SortableNoteCategoryRowProps> = ({
  cat,
  renameNoteCategory,
  deleteNoteCategory,
  toggleLockNoteCategory = () => {},
}) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: cat.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };
  const isLocked = !!cat.locked;

  return (
    <NoteCategoryItem ref={setNodeRef} style={style}>
      <Input
        value={cat.name}
        onChange={e => renameNoteCategory(cat.id, e.target.value)}
      />
      <LockButtonTooltipWrapper data-tooltip={isLocked ? '잠금' : '잠금 해제'}>
        <LockButton
          type="button"
          $locked={isLocked}
          onClick={() => toggleLockNoteCategory(cat.id)}
          title={isLocked ? '잠금 해제' : '잠금'}
          aria-label={isLocked ? '잠금 해제' : '잠금'}
        >
          {isLocked ? (
            <img src="/lock.svg" alt="잠금" width={14} height={14} />
          ) : (
            <img src="/unlock.svg" alt="잠금 해제" width={14} height={14} />
          )}
        </LockButton>
      </LockButtonTooltipWrapper>
        <DangerButton onClick={() => deleteNoteCategory(cat.id)} title="삭제">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </DangerButton>
      <DragHandle {...attributes} {...listeners} aria-label="노트북 카테고리 순서 조정" />
    </NoteCategoryItem>
  );
};

interface Props {
  isOpen: boolean;
  onClose: () => void;
  user: User;
  currentClass: Class | null;
  onDefaultPointsChange?: (points: number) => void; // 기본 포인트 변경 콜백
  currentDefaultPoints?: number; // 현재 설정된 기본 포인트
  onStatusDefaultPointsChange?: (points: { pending: number; confirmed: number; delayed: number; exempted: number }) => void; // 상태별 기본 포인트 변경 콜백
  currentStatusDefaultPoints?: { pending: number; confirmed: number; delayed: number; exempted: number }; // 현재 설정된 상태별 기본 포인트
  onCategoriesChange?: (categories: AssignmentCategory[]) => void; // 카테고리 변경 콜백
  onAssignmentSettingsChange?: (defaultPoints: number, statusDefaultPoints: { pending: number; confirmed: number; delayed: number; exempted: number }) => void; // 과제 설정 변경 콜백
  onCoinSettingsChange?: (coinDefaultPoints: number) => void; // 코인 설정 변경 콜백
  currentCoinDefaultPoints?: number; // 현재 설정된 코인 기본 단위
  onLicenseClick?: () => void; // 이용권 구매 클릭 콜백
  // 타임 태그 섹션용
  initialTimeTags?: TimeTag[];
  alarmSets?: AlarmSet[];
  onCreateAlarms?: (alarms: { alarm: Alarm; setIndex: number; dayIndex: number }[]) => Promise<void>;
  onTimeTagsSave?: (timeTags: TimeTag[]) => void | Promise<void>;
}

const AssignmentSettingsModal: React.FC<Props> = ({ isOpen, onClose, user, currentClass, onDefaultPointsChange, currentDefaultPoints = 10, onStatusDefaultPointsChange, currentStatusDefaultPoints, onCategoriesChange, onAssignmentSettingsChange, onCoinSettingsChange, currentCoinDefaultPoints = 10, onLicenseClick, initialTimeTags = [], alarmSets = [], onCreateAlarms, onTimeTagsSave }) => {
  // 전역 스피너 제거 스타일 추가
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      input[type="number"]::-webkit-outer-spin-button,
      input[type="number"]::-webkit-inner-spin-button {
        -webkit-appearance: none !important;
        margin: 0 !important;
        display: none !important;
      }
      input[type="number"] {
        -moz-appearance: textfield !important;
        appearance: textfield !important;
      }
    `;
    document.head.appendChild(style);
    
    return () => {
      document.head.removeChild(style);
    };
  }, []);
  const [categories, setCategories] = useState<AssignmentCategory[]>([]);
  const [localCategories, setLocalCategories] = useState<AssignmentCategory[]>([]); // 로컬 편집용
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_COLORS[0]);
  const [colorPickerOpen, setColorPickerOpen] = useState<string | null>(null); // 열린 색상 선택기 ID
  const [popupPosition, setPopupPosition] = useState({ x: 0, y: 0 }); // 팝업 위치
  const [hasChanges, setHasChanges] = useState(false); // 변경사항 여부
  const [isSaving, setIsSaving] = useState(false); // 저장 중 상태

  // 노트 카테고리 관련 상태
  const [noteCategories, setNoteCategories] = useState<NotebookCategory[]>([]);
  const [localNoteCategories, setLocalNoteCategories] = useState<NotebookCategory[]>([]); // 로컬 편집용
  const [newNoteCategoryName, setNewNoteCategoryName] = useState('');
  const [hasNoteCategoryChanges, setHasNoteCategoryChanges] = useState(false); // 노트 카테고리 변경사항 여부

  // 노트 카테고리 삭제 확인 모달 상태
  const [showDeleteCategoryConfirm, setShowDeleteCategoryConfirm] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState<NotebookCategory | null>(null);
  const [categoryNotesCount, setCategoryNotesCount] = useState(0);

  // 노트북 잠금 비밀번호 설정 모달 (잠금 시)
  const [showLockPasswordModal, setShowLockPasswordModal] = useState(false);
  const [lockPasswordCategory, setLockPasswordCategory] = useState<NotebookCategory | null>(null);
  const [lockPasswordValue, setLockPasswordValue] = useState('');
  const [lockPasswordConfirm, setLockPasswordConfirm] = useState('');
  const [lockPasswordError, setLockPasswordError] = useState('');
  // 노트북 잠금 해제용 계정 비밀번호 모달
  const [showUnlockConfirmModal, setShowUnlockConfirmModal] = useState(false);
  const [unlockCategory, setUnlockCategory] = useState<NotebookCategory | null>(null);
  const [unlockAccountPassword, setUnlockAccountPassword] = useState('');
  const [unlockError, setUnlockError] = useState('');
  // 저장 전까지 유지할 잠금 비밀번호 (categoryId -> 비밀번호). 저장 버튼 클릭 시 해시해서 서버에 반영
  const [pendingLockPasswords, setPendingLockPasswords] = useState<Record<string, string>>({});

  // 개별 기록 열람 잠금 설정
  const [individualRecordViewLocked, setIndividualRecordViewLocked] = useState(false);
  const [localIndividualRecordViewLocked, setLocalIndividualRecordViewLocked] = useState(false);
  const [hasIndividualRecordViewChanges, setHasIndividualRecordViewChanges] = useState(false);
  const [showRecordLockPasswordModal, setShowRecordLockPasswordModal] = useState(false);
  const [recordLockPasswordValue, setRecordLockPasswordValue] = useState('');
  const [recordLockPasswordConfirm, setRecordLockPasswordConfirm] = useState('');
  const [recordLockPasswordError, setRecordLockPasswordError] = useState('');
  const [showRecordUnlockModal, setShowRecordUnlockModal] = useState(false);
  const [recordUnlockAccountPassword, setRecordUnlockAccountPassword] = useState('');
  const [recordUnlockError, setRecordUnlockError] = useState('');
  const [pendingRecordLockPassword, setPendingRecordLockPassword] = useState<string | null>(null);

  // 라이센스 제한 모달 상태
  const [showLicenseLimitModal, setShowLicenseLimitModal] = useState(false);
  const [licenseLimitMessage, setLicenseLimitMessage] = useState('');

  // 드래그 센서 (카테고리/노트북 카테고리 공용)
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // SortableAssignmentCategoryRow / SortableNoteCategoryRow 는 파일 상단 모듈 스코프에 정의됨.
  // 모달 내부에서 컴포넌트를 매 렌더마다 새로 선언하면 타입이 바뀌어 입력란이 리마운트되며 포커스가 끊깁니다.

  // 다른 클래스 데이터 가져오기 모달 상태 (과제 카테고리)
  const [showImportModal, setShowImportModal] = useState(false);
  const [availableClasses, setAvailableClasses] = useState<Class[]>([]);
  const [selectedSourceClassId, setSelectedSourceClassId] = useState<string>('');
  const [sourceCategories, setSourceCategories] = useState<AssignmentCategory[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
  const [isLoadingSourceCategories, setIsLoadingSourceCategories] = useState(false);
  const [isClassDropdownOpen, setIsClassDropdownOpen] = useState(false);
  
  // 다른 클래스 데이터 가져오기 모달 상태 (노트 카테고리)
  const [showNoteImportModal, setShowNoteImportModal] = useState(false);
  const [availableClassesForNote, setAvailableClassesForNote] = useState<Class[]>([]);
  const [selectedSourceClassIdForNote, setSelectedSourceClassIdForNote] = useState<string>('');
  const [sourceNoteCategories, setSourceNoteCategories] = useState<NotebookCategory[]>([]);
  const [selectedNoteCategoryIds, setSelectedNoteCategoryIds] = useState<Set<string>>(new Set());
  const [isLoadingSourceNoteCategories, setIsLoadingSourceNoteCategories] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [isNoteClassDropdownOpen, setIsNoteClassDropdownOpen] = useState(false);
  
  // 클래스 연동 설정 관련 상태
  const [isImportButtonEnabled, setIsImportButtonEnabled] = useState(() => {
    const saved = localStorage.getItem('isImportButtonEnabled');
    return saved !== null ? saved === 'true' : false; // 기본값: off
  });
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncAvailableClasses, setSyncAvailableClasses] = useState<Class[]>([]);
  const [syncSelectedClassId, setSyncSelectedClassId] = useState<string>('');
  const [isSyncClassDropdownOpen, setIsSyncClassDropdownOpen] = useState(false);
  const [showSyncConfirmModal, setShowSyncConfirmModal] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  
  // 과제 포인트 설정 관련 상태
  const [defaultPoints, setDefaultPoints] = useState<number>(currentDefaultPoints);
  const [localDefaultPoints, setLocalDefaultPoints] = useState<number>(currentDefaultPoints);
  const [hasPointChanges, setHasPointChanges] = useState(false);
  
  // 과제 상태별 기본 지급 포인트 설정
  const [, setStatusDefaultPoints] = useState({
    pending: 0,      // 미확인
    confirmed: 0,    // 확인
    delayed: 0,      // 지연확인
    exempted: 0      // 예외
  });
  const [localStatusDefaultPoints, setLocalStatusDefaultPoints] = useState({
    pending: 0,      // 미확인
    confirmed: 0,    // 확인
    delayed: 0,      // 지연확인 (0–100 백분율)
    exempted: 0      // 예외 (0–100 백분율)
  });
  const [delayedPercentDisplay, setDelayedPercentDisplay] = useState<string>('0');
  const [exemptedPercentDisplay, setExemptedPercentDisplay] = useState<string>('0');

  // 코인 설정 관련 상태
  const [coinDefaultPoints, setCoinDefaultPoints] = useState<number>(currentCoinDefaultPoints);
  const [localCoinDefaultPoints, setLocalCoinDefaultPoints] = useState<number>(currentCoinDefaultPoints);
  const [hasCoinChanges, setHasCoinChanges] = useState(false);
  
  // 개별 접근 페이지 설정 관련 상태
  const [routineSectionEnabled, setRoutineSectionEnabled] = useState<boolean>(true);
  const [storeIndividualPurchaseEnabled, setStoreIndividualPurchaseEnabled] = useState<boolean>(true);
  const [hasIndividualAccessChanges, setHasIndividualAccessChanges] = useState(false);

  // 타임 태그 섹션 관련 상태
  const [localTimeTags, setLocalTimeTags] = useState<TimeTag[]>(initialTimeTags);
  const [hasTimeTagChanges, setHasTimeTagChanges] = useState(false);

  // 클래스챗 게스트 입장 설정 관련 상태
  const [guestEntryEnabled, setGuestEntryEnabled] = useState<boolean>(false);
  const [localGuestEntryEnabled, setLocalGuestEntryEnabled] = useState<boolean>(false);
  const [hasGuestEntryChanges, setHasGuestEntryChanges] = useState(false);
  
  // 취소 확인 모달 상태
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setShowCancelConfirm(false);
    }
  }, [isOpen]);

  // 로컬 스토리지 변경 감지 (다른 클래스 데이터 가져오기 버튼 활성화 상태)
  useEffect(() => {
    const handleStorageChange = () => {
      const saved = localStorage.getItem('isImportButtonEnabled');
      setIsImportButtonEnabled(saved !== null ? saved === 'true' : false);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('importButtonEnabledChanged', handleStorageChange);

    // 주기적으로 확인 (같은 탭에서의 변경 감지용)
    const interval = setInterval(() => {
      const saved = localStorage.getItem('isImportButtonEnabled');
      const currentValue = saved !== null ? saved === 'true' : false;
      setIsImportButtonEnabled(prev => {
        if (prev !== currentValue) {
          return currentValue;
        }
        return prev;
      });
    }, 100);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('importButtonEnabledChanged', handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const load = async () => {
      if (!isOpen || !user?.id || !currentClass?.id) return;
      
      // 과제 카테고리 로드 (항상 서버에서)
      const list = await assignmentCategoryService.loadCategories(user.id, currentClass.id);
      setCategories(list);
      setLocalCategories([...list]); // 로컬 카테고리 초기화
      setHasChanges(false); // 변경사항 초기화
      
      // 사용자 기본 설정 (클래스 공통) 로드
      const userDoc = await getDoc(doc(db, `users/${user.id}`));
      const userGuestEntryEnabled = userDoc.exists()
        ? userDoc.data()?.guestEntryEnabled === true
        : false;

      // 캐시 값이 기본값(초기값)인지 확인
      const isDefaultCache = currentDefaultPoints === 10 && 
        currentStatusDefaultPoints?.pending === 0 &&
        currentStatusDefaultPoints?.confirmed === 0 &&
        currentStatusDefaultPoints?.delayed === 0 &&
        currentStatusDefaultPoints?.exempted === 0;
      
      // 캐시가 기본값이면 서버에서 로드, 아니면 캐시 사용
      if (isDefaultCache) {
        // 서버에서 DefaultSet 로드
        const defaultSet = await defaultSetService.loadDefaultSet(user.id, currentClass.id);
        
        // 과제 기본 포인트 설정 (서버 값 우선, 없으면 기본값)
        const serverDefaultPoints = defaultSet?.assignmentSettings?.defaultPoints ?? 10;
        const serverStatusDefaultPoints = defaultSet?.assignmentSettings?.statusDefaultPoints ?? {
          pending: 0,
          confirmed: 10,
          delayed: 0,
          exempted: 0
        };
        
        setDefaultPoints(serverDefaultPoints);
        setLocalDefaultPoints(serverDefaultPoints);
        
        setStatusDefaultPoints(serverStatusDefaultPoints);
        setLocalStatusDefaultPoints(serverStatusDefaultPoints);
        setDelayedPercentDisplay(String(serverStatusDefaultPoints.delayed ?? 0));
        setExemptedPercentDisplay(String(serverStatusDefaultPoints.exempted ?? 0));
        
        // 부모 컴포넌트에도 알림 (캐시 업데이트)
        if (onDefaultPointsChange) onDefaultPointsChange(serverDefaultPoints);
        if (onStatusDefaultPointsChange) onStatusDefaultPointsChange(serverStatusDefaultPoints);

        // 코인 설정 (서버 값 우선, 없으면 기본값)
        const serverCoinDefaultPoints = defaultSet?.coinSettings?.defaultPoints ?? 10;
        setCoinDefaultPoints(serverCoinDefaultPoints);
        setLocalCoinDefaultPoints(serverCoinDefaultPoints);
        if (onCoinSettingsChange) onCoinSettingsChange(serverCoinDefaultPoints);

        // 개별 접근 페이지 설정 로드
        const serverRoutineSectionEnabled = defaultSet?.routineSectionEnabled !== undefined ? defaultSet.routineSectionEnabled : true;
        const serverStoreIndividualPurchaseEnabled = defaultSet?.storeIndividualPurchaseEnabled !== undefined ? defaultSet.storeIndividualPurchaseEnabled : true;
        setRoutineSectionEnabled(serverRoutineSectionEnabled);
        setStoreIndividualPurchaseEnabled(serverStoreIndividualPurchaseEnabled);
        // 개별 기록 열람 잠금 설정 로드
        const serverIndividualRecordViewLocked = defaultSet?.individualRecordViewLocked === true;
        setIndividualRecordViewLocked(serverIndividualRecordViewLocked);
        setLocalIndividualRecordViewLocked(serverIndividualRecordViewLocked);
      } else {
        // 캐시 값 사용
        setDefaultPoints(currentDefaultPoints);
        setLocalDefaultPoints(currentDefaultPoints);
        
        if (currentStatusDefaultPoints) {
          setStatusDefaultPoints(currentStatusDefaultPoints);
          setLocalStatusDefaultPoints(currentStatusDefaultPoints);
          setDelayedPercentDisplay(String(currentStatusDefaultPoints.delayed ?? 0));
          setExemptedPercentDisplay(String(currentStatusDefaultPoints.exempted ?? 0));
        }
        
        setCoinDefaultPoints(currentCoinDefaultPoints);
        setLocalCoinDefaultPoints(currentCoinDefaultPoints);

        // 개별 접근 페이지 설정도 서버에서 로드
        const defaultSet = await defaultSetService.loadDefaultSet(user.id, currentClass.id);
        const serverRoutineSectionEnabled = defaultSet?.routineSectionEnabled !== undefined ? defaultSet.routineSectionEnabled : true;
        const serverStoreIndividualPurchaseEnabled = defaultSet?.storeIndividualPurchaseEnabled !== undefined ? defaultSet.storeIndividualPurchaseEnabled : true;
        setRoutineSectionEnabled(serverRoutineSectionEnabled);
        setStoreIndividualPurchaseEnabled(serverStoreIndividualPurchaseEnabled);
        const serverIndividualRecordViewLocked = defaultSet?.individualRecordViewLocked === true;
        setIndividualRecordViewLocked(serverIndividualRecordViewLocked);
        setLocalIndividualRecordViewLocked(serverIndividualRecordViewLocked);
      }
      
      setHasPointChanges(false);
      setHasCoinChanges(false);
      setHasIndividualAccessChanges(false);
      setHasTimeTagChanges(false);
      setGuestEntryEnabled(userGuestEntryEnabled);
      setLocalGuestEntryEnabled(userGuestEntryEnabled);
      setHasGuestEntryChanges(false);

      // 타임 태그 초기화 (부모에서 전달)
      setLocalTimeTags(initialTimeTags || []);

      // 노트 카테고리 로드 (항상 서버에서)
      if (user?.id && currentClass?.id) {
        const noteCats = await boardService.getCategories(user.id, currentClass.id);
        setNoteCategories(noteCats);
        setLocalNoteCategories([...noteCats]);
        setHasNoteCategoryChanges(false);
        setPendingLockPasswords({});
        setPendingRecordLockPassword(null);
        setHasIndividualRecordViewChanges(false);
      }
    };
    load();
    // 모달 열림/클래스·유저 변경 시에만 재실행. 콜백/캐시값을 넣으면 카테고리 추가 직후 부모 리렌더로 effect가 다시 돌아 서버 목록이 localCategories를 덮어써 추가분이 사라짐.
  }, [currentClass?.id, isOpen, user?.id, initialTimeTags]);

  // 과제 포인트 변경 처리
  const handleDefaultPointsChange = (value: number) => {
    setLocalDefaultPoints(value);
    setHasPointChanges(value !== defaultPoints);
  };

  // 과제 상태별 기본 지급 포인트 변경 처리 (지연확인/예외는 0–100 백분율)
  const handleStatusDefaultPointsChange = (status: keyof typeof localStatusDefaultPoints, value: string) => {
    const num = value === '' ? 0 : Number(value) || 0;
    const clamped = (status === 'delayed' || status === 'exempted')
      ? Math.min(100, Math.max(0, num))
      : num;
    setLocalStatusDefaultPoints(prev => ({
      ...prev,
      [status]: clamped
    }));
    if (status === 'delayed') setDelayedPercentDisplay(value);
    if (status === 'exempted') setExemptedPercentDisplay(value);
    setHasPointChanges(true);
  };

  // 코인 설정 변경 처리
  const handleCoinDefaultPointsChange = (value: number) => {
    setLocalCoinDefaultPoints(value);
    setHasCoinChanges(value !== coinDefaultPoints);
  };

  // 타임 태그 섹션 변경 콜백 (useCallback으로 참조 안정화 - 무한 루프 방지)
  const handleTimeTagsChange = useCallback((tags: TimeTag[]) => {
    setLocalTimeTags(tags);
    setHasTimeTagChanges(true);
  }, []);

  const addCategory = () => {
    if (!newName.trim()) return;
    const cat: AssignmentCategory = { 
      id: `cat_${Date.now()}`, 
      name: newName.trim(), 
      color: newColor,
      createdAt: new Date() 
    };
    const newCategories = [...localCategories, cat];
    setLocalCategories(newCategories);
    setNewName('');
    setNewColor(DEFAULT_COLORS[0]);
    setHasChanges(true);
    
    // 부모 컴포넌트에 카테고리 변경 알림
    if (onCategoriesChange) {
      onCategoriesChange(newCategories);
    }
  };

  const renameCategory = (id: string, name: string) => {
    const category = localCategories.find(c => c.id === id);
    if (!category) return;
    const updated = { ...category, name, updatedAt: new Date() };
    const newCategories = localCategories.map(c => c.id === id ? updated : c);
    setLocalCategories(newCategories);
    setHasChanges(true);
    
    // 부모 컴포넌트에 카테고리 변경 알림
    if (onCategoriesChange) {
      onCategoriesChange(newCategories);
    }
  };

  const updateCategoryColor = (id: string, color: string) => {
    const category = localCategories.find(c => c.id === id);
    if (!category) return;
    const updated = { ...category, color, updatedAt: new Date() };
    const newCategories = localCategories.map(c => c.id === id ? updated : c);
    setLocalCategories(newCategories);
    setColorPickerOpen(null); // 색상 선택 후 팝업 닫기
    setHasChanges(true);
    
    // 부모 컴포넌트에 카테고리 변경 알림
    if (onCategoriesChange) {
      onCategoriesChange(newCategories);
    }
  };

  const deleteCategory = (id: string) => {
    const newCategories = localCategories.filter(c => c.id !== id);
    setLocalCategories(newCategories);
    setHasChanges(true);
    
    // 부모 컴포넌트에 카테고리 변경 알림
    if (onCategoriesChange) {
      onCategoriesChange(newCategories);
    }
  };

  // 노트 카테고리 추가
  const addNoteCategory = () => {
    if (!newNoteCategoryName.trim()) return;
    
    // 노트 카테고리 개수 제한 체크
    const limitCheck = checkNoteCategoryLimit(user, localNoteCategories.length);
    if (!limitCheck.allowed) {
      setLicenseLimitMessage(limitCheck.message || '');
      setShowLicenseLimitModal(true);
      return;
    }
    
    // 임시 노트북 생성 (저장 시 서버에서 실제 ID와 Timestamp 생성)
    const cat: NotebookCategory = { 
      id: `note_cat_${Date.now()}`, 
      name: newNoteCategoryName.trim(), 
      order: localNoteCategories.length,
      createdAt: null as any // 저장 시 서버에서 생성됨
    };
    const newCategories = [...localNoteCategories, cat];
    setLocalNoteCategories(newCategories);
    setNewNoteCategoryName('');
    setHasNoteCategoryChanges(true);
  };

  // 노트 카테고리 이름 변경
  const renameNoteCategory = (id: string, name: string) => {
    const category = localNoteCategories.find(c => c.id === id);
    if (!category) return;
    const updated = { ...category, name };
    const newCategories = localNoteCategories.map(c => c.id === id ? updated : c);
    setLocalNoteCategories(newCategories);
    setHasNoteCategoryChanges(true);
  };

  // 노트 카테고리 잠금: 잠금 비밀번호 설정 모달 열기 (저장 버튼 누를 때까지 서버 반영 안 함)
  const openLockPasswordModal = (category: NotebookCategory) => {
    setLockPasswordCategory(category);
    setLockPasswordValue('');
    setLockPasswordConfirm('');
    setLockPasswordError('');
    setShowLockPasswordModal(true);
  };

  // 노트 카테고리 잠금 해제: 계정 비밀번호 입력 모달 열기 (저장 버튼 누를 때까지 서버 반영 안 함)
  const openUnlockConfirmModal = (category: NotebookCategory) => {
    setUnlockCategory(category);
    setUnlockAccountPassword('');
    setUnlockError('');
    setShowUnlockConfirmModal(true);
  };

  const toggleLockNoteCategory = (id: string) => {
    const category = localNoteCategories.find(c => c.id === id);
    if (!category) return;
    if (category.locked) {
      openUnlockConfirmModal(category);
    } else {
      openLockPasswordModal(category);
    }
  };

  // 잠금 비밀번호 설정 모달 확인: 로컬 상태만 변경, 저장 시 반영
  const confirmLockPassword = () => {
    if (!lockPasswordCategory) return;
    const pw = lockPasswordValue.trim();
    const confirm = lockPasswordConfirm.trim();
    if (pw.length < 4) {
      setLockPasswordError('잠금 비밀번호는 4자 이상 입력해 주세요.');
      return;
    }
    if (pw !== confirm) {
      setLockPasswordError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setPendingLockPasswords(prev => ({ ...prev, [lockPasswordCategory.id]: pw }));
    setLocalNoteCategories(prev =>
      prev.map(c => (c.id === lockPasswordCategory.id ? { ...c, locked: true } : c))
    );
    setHasNoteCategoryChanges(true);
    setShowLockPasswordModal(false);
    setLockPasswordCategory(null);
    setLockPasswordValue('');
    setLockPasswordConfirm('');
    setLockPasswordError('');
  };

  // 잠금 해제(계정 비밀번호) 모달 확인: 재인증 후 로컬 상태만 변경, 저장 시 반영
  const confirmUnlockWithAccountPassword = async () => {
    if (!unlockCategory || !user?.email) return;
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      setUnlockError('로그인 세션을 찾을 수 없습니다.');
      return;
    }
    if (!unlockAccountPassword.trim()) {
      setUnlockError('계정 암호를 입력해 주세요.');
      return;
    }
    try {
      const credential = EmailAuthProvider.credential(user.email, unlockAccountPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      setLocalNoteCategories(prev =>
        prev.map(c => (c.id === unlockCategory.id ? { ...c, locked: false } : c))
      );
      setPendingLockPasswords(prev => {
        const next = { ...prev };
        delete next[unlockCategory.id];
        return next;
      });
      setHasNoteCategoryChanges(true);
      setShowUnlockConfirmModal(false);
      setUnlockCategory(null);
      setUnlockAccountPassword('');
      setUnlockError('');
    } catch (err: any) {
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        setUnlockError('비밀번호가 올바르지 않습니다.');
      } else {
        setUnlockError(err?.message || '인증에 실패했습니다.');
      }
    }
  };

  // 개별 기록 열람 잠금 토글: 잠금 시 비밀번호 설정 모달, 해제 시 계정 비밀번호 모달
  const toggleRecordViewLock = () => {
    if (localIndividualRecordViewLocked) {
      setShowRecordUnlockModal(true);
      setRecordUnlockAccountPassword('');
      setRecordUnlockError('');
    } else {
      setShowRecordLockPasswordModal(true);
      setRecordLockPasswordValue('');
      setRecordLockPasswordConfirm('');
      setRecordLockPasswordError('');
    }
  };

  const confirmRecordLockPassword = () => {
    if (recordLockPasswordValue.length < 4) {
      setRecordLockPasswordError('4자 이상 입력해 주세요.');
      return;
    }
    if (recordLockPasswordValue !== recordLockPasswordConfirm) {
      setRecordLockPasswordError('비밀번호가 일치하지 않습니다.');
      return;
    }
    setPendingRecordLockPassword(recordLockPasswordValue);
    setLocalIndividualRecordViewLocked(true);
    setHasIndividualRecordViewChanges(true);
    setShowRecordLockPasswordModal(false);
    setRecordLockPasswordValue('');
    setRecordLockPasswordConfirm('');
    setRecordLockPasswordError('');
  };

  const confirmRecordUnlockWithAccountPassword = async () => {
    if (!user?.email) return;
    const firebaseUser = auth.currentUser;
    if (!firebaseUser) {
      setRecordUnlockError('로그인 세션을 찾을 수 없습니다.');
      return;
    }
    if (!recordUnlockAccountPassword.trim()) {
      setRecordUnlockError('계정 암호를 입력해 주세요.');
      return;
    }
    try {
      const credential = EmailAuthProvider.credential(user.email, recordUnlockAccountPassword);
      await reauthenticateWithCredential(firebaseUser, credential);
      setLocalIndividualRecordViewLocked(false);
      setPendingRecordLockPassword(null);
      setHasIndividualRecordViewChanges(true);
      setShowRecordUnlockModal(false);
      setRecordUnlockAccountPassword('');
      setRecordUnlockError('');
    } catch (err: any) {
      if (err?.code === 'auth/wrong-password' || err?.code === 'auth/invalid-credential') {
        setRecordUnlockError('비밀번호가 올바르지 않습니다.');
      } else {
        setRecordUnlockError(err?.message || '인증에 실패했습니다.');
      }
    }
  };

  // 노트 카테고리 삭제
  const deleteNoteCategory = async (id: string) => {
    if (!currentClass) return;

    try {
      // 해당 카테고리에 속한 노트 개수 확인
      const notes = await boardService.getNotes(user.id, currentClass.id, id);
      const notesCount = notes.length;

      if (notesCount > 0) {
        // 노트가 있는 경우 경고 모달 표시
        const category = localNoteCategories.find(c => c.id === id);
        if (category) {
          setCategoryToDelete(category);
          setCategoryNotesCount(notesCount);
          setShowDeleteCategoryConfirm(true);
        }
      } else {
        // 노트가 없는 경우 바로 삭제
        const newCategories = localNoteCategories.filter(c => c.id !== id);
        setLocalNoteCategories(newCategories);
        setHasNoteCategoryChanges(true);
      }
    } catch (error) {
      console.error('카테고리 삭제 전 노트 확인 실패:', error);
      // 에러가 발생한 경우에도 삭제 진행
      const newCategories = localNoteCategories.filter(c => c.id !== id);
      setLocalNoteCategories(newCategories);
      setHasNoteCategoryChanges(true);
    }
  };

  // 카테고리 삭제 확인
  const confirmDeleteCategory = () => {
    if (categoryToDelete) {
      const newCategories = localNoteCategories.filter(c => c.id !== categoryToDelete.id);
      setLocalNoteCategories(newCategories);
      setHasNoteCategoryChanges(true);
    }
    setShowDeleteCategoryConfirm(false);
    setCategoryToDelete(null);
    setCategoryNotesCount(0);
  };

  // 카테고리 삭제 취소
  const cancelDeleteCategory = () => {
    setShowDeleteCategoryConfirm(false);
    setCategoryToDelete(null);
    setCategoryNotesCount(0);
  };

  const openColorPicker = (id: string, event: React.MouseEvent<HTMLElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const popupWidth = 140; // 팝업 너비
    const popupHeight = 80; // 팝업 높이 (대략적)
    
    // 팝업이 왼쪽에 나타나도록 위치 계산
    let x = rect.left - popupWidth - 10; // 버튼 왼쪽에 10px 간격
    let y = rect.top + rect.height / 2 - popupHeight / 2; // 버튼 중앙에 맞춤
    
    // 왼쪽 공간이 부족하면 오른쪽에 표시
    if (x < 20) {
      x = rect.right + 10;
    }
    
    // 위쪽 공간이 부족하면 아래쪽에 표시
    if (y < 20) {
      y = rect.bottom + 10;
    }
    
    // 아래쪽 공간이 부족하면 위쪽에 표시
    if (y + popupHeight > window.innerHeight - 20) {
      y = rect.top - popupHeight - 10;
    }
    
    setPopupPosition({ x, y });
    setColorPickerOpen(id);
  };

  const saveChanges = async () => {
    if (!user?.id || !currentClass?.id) return;
    if (isSaving) return; // 이미 저장 중이면 중복 실행 방지

    // 변경사항이 없는 경우 알림 표시
    if (!hasChanges && !hasPointChanges && !hasCoinChanges && !hasNoteCategoryChanges && !hasIndividualAccessChanges && !hasIndividualRecordViewChanges && !hasGuestEntryChanges && !hasTimeTagChanges) {
      alert('저장할 변경사항이 없습니다.');
      return;
    }

    // 즉시 UI 업데이트
    flushSync(() => {
      setIsSaving(true);
    });

    try {
      // 과제 기본 포인트 저장
      if (hasPointChanges) {
        setDefaultPoints(localDefaultPoints);
        if (onDefaultPointsChange) {
          onDefaultPointsChange(localDefaultPoints);
        }
        
        // 과제 상태별 기본 지급 포인트 저장
        if (onStatusDefaultPointsChange) {
          onStatusDefaultPointsChange(localStatusDefaultPoints);
        }
        
        // 서버에 과제 설정 저장
        if (onAssignmentSettingsChange) {
          onAssignmentSettingsChange(localDefaultPoints, localStatusDefaultPoints);
        }
        
        setHasPointChanges(false);
      }

      // 코인 설정 저장
      if (hasCoinChanges) {
        setCoinDefaultPoints(localCoinDefaultPoints);
        if (onCoinSettingsChange) {
          onCoinSettingsChange(localCoinDefaultPoints);
        }
        setHasCoinChanges(false);
      }
      
      // 과제 카테고리 전체를 한 번에 저장 (순서 포함)
      if (hasChanges) {
        await defaultSetService.updateDefaultSet(user.id, currentClass.id, {
          assignmentCategories: localCategories,
        });

        // 성공 시 상태 업데이트 (현재 로컬 순서를 그대로 유지)
        setCategories([...localCategories]);
        setHasChanges(false);
      }

      // 노트 카테고리 저장 (순서 및 이름 반영)
      if (hasNoteCategoryChanges) {
        // 새 카테고리 생성
        const newNoteCategories = localNoteCategories.filter(c => c.id.startsWith('note_cat_'));
        for (const category of newNoteCategories) {
          await boardService.createCategory(user.id, currentClass.id, category.name);
        }

        // 삭제된 카테고리 처리 (기존 카테고리 중에서 localNoteCategories에 없는 것들)
        const deletedNoteCategories = noteCategories.filter(
          c => !localNoteCategories.find(lc => lc.id === c.id)
        );
        for (const category of deletedNoteCategories) {
          await boardService.deleteCategoryWithNotes(user.id, currentClass.id, category.id);
        }

        // 이름 변경·순서를 id 기준으로 Firestore에 반영 (이름만으로 정렬하면 변경된 이름이 저장되지 않음)
        await boardService.applyNoteBookCategoriesFromLocal(
          user.id,
          currentClass.id,
          localNoteCategories
        );

        // 노트북 잠금 상태 및 잠금 비밀번호 해시 반영 (저장 시에만 적용)
        const existingIds = localNoteCategories.filter(c => !c.id.startsWith('note_cat_')).map(c => c.id);
        if (existingIds.length > 0) {
          const lockUpdates: Array<{ categoryId: string; locked: boolean; lockPasswordHash?: string | null }> = [];
          for (const c of localNoteCategories) {
            if (c.id.startsWith('note_cat_')) continue;
            const newHash =
              c.locked && pendingLockPasswords[c.id]
                ? await hashLockPassword(pendingLockPasswords[c.id])
                : undefined;
            lockUpdates.push({
              categoryId: c.id,
              locked: !!c.locked,
              lockPasswordHash: c.locked ? newHash : null,
            });
          }
          await boardService.setNoteBookCategoriesLockState(user.id, currentClass.id, lockUpdates);
          setPendingLockPasswords({});
        }

        // 성공 시 상태 업데이트 (이름/순서를 UI 상태에 그대로 유지)
        setNoteCategories([...localNoteCategories]);
        setHasNoteCategoryChanges(false);
      }

      // 타임 태그 저장
      if (hasTimeTagChanges) {
        await TimeTagService.saveTimeTags(user.id, currentClass.id, localTimeTags);
        setHasTimeTagChanges(false);
        if (onTimeTagsSave) {
          onTimeTagsSave(localTimeTags);
        }
      }

      // 개별 접근 페이지 설정 저장
      if (hasIndividualAccessChanges) {
        console.log('💾 개별 접근 페이지 설정 저장 시작:', {
          routineSectionEnabled,
          storeIndividualPurchaseEnabled,
          userId: user.id,
          classId: currentClass.id
        });
        await defaultSetService.updateIndividualAccessSettings(user.id, currentClass.id, {
          routineSectionEnabled,
          storeIndividualPurchaseEnabled
        });
        console.log('✅ 개별 접근 페이지 설정 저장 완료');
        setHasIndividualAccessChanges(false);
      }

      // 개별 기록 열람 잠금 설정 저장
      if (hasIndividualRecordViewChanges) {
        const newHash =
          localIndividualRecordViewLocked && pendingRecordLockPassword
            ? await hashLockPassword(pendingRecordLockPassword)
            : undefined;
        await defaultSetService.updateIndividualRecordViewLock(user.id, currentClass.id, {
          individualRecordViewLocked: localIndividualRecordViewLocked,
          individualRecordViewLockPasswordHash: localIndividualRecordViewLocked ? (newHash ?? null) : null,
        });
        setIndividualRecordViewLocked(localIndividualRecordViewLocked);
        setPendingRecordLockPassword(null);
        setHasIndividualRecordViewChanges(false);
      }

      // 클래스챗 게스트 입장 설정 저장 (사용자 공통)
      if (hasGuestEntryChanges) {
        await setDoc(
          doc(db, `users/${user.id}`),
          {
            guestEntryEnabled: localGuestEntryEnabled,
            updatedAt: new Date()
          },
          { merge: true }
        );
        setGuestEntryEnabled(localGuestEntryEnabled);
        setHasGuestEntryChanges(false);
      }
      
      onClose();
    } catch (error) {
      console.error('설정 저장 중 오류 발생:', error);
      alert('설정 저장 중 오류가 발생했습니다.');
    } finally {
      flushSync(() => {
        setIsSaving(false);
      });
    }
  };

  // 다른 클래스 데이터 가져오기 모달 열기
  const handleOpenImportModal = async () => {
    if (!user?.id || !currentClass?.id) return;
    
    try {
      // 현재 클래스를 제외한 모든 클래스 목록 가져오기
      const allClasses = await userDataService.loadUserClasses(user.id);
      const filteredClasses = allClasses.filter(c => c.id !== currentClass.id);
      setAvailableClasses(filteredClasses);
      setShowImportModal(true);
      setSelectedSourceClassId('');
      setSourceCategories([]);
      setSelectedCategoryIds(new Set());
    } catch (error) {
      console.error('클래스 목록 로드 실패:', error);
      alert('클래스 목록을 불러오는데 실패했습니다.');
    }
  };

  // 다른 클래스 데이터 가져오기 모달 닫기
  const handleCloseImportModal = () => {
    setShowImportModal(false);
    setSelectedSourceClassId('');
    setSourceCategories([]);
    setSelectedCategoryIds(new Set());
  };

  // 소스 클래스 선택 시 카테고리 로드
  const handleSourceClassChange = async (classId: string) => {
    if (!user?.id || !classId) return;
    
    setSelectedSourceClassId(classId);
    setIsClassDropdownOpen(false);
    setIsLoadingSourceCategories(true);
    setSourceCategories([]);
    setSelectedCategoryIds(new Set());
    
    try {
      const categories = await assignmentCategoryService.loadCategories(user.id, classId);
      setSourceCategories(categories);
    } catch (error) {
      console.error('카테고리 로드 실패:', error);
      alert('카테고리를 불러오는데 실패했습니다.');
    } finally {
      setIsLoadingSourceCategories(false);
    }
  };

  // 카테고리 선택/해제
  const handleToggleCategorySelection = (categoryId: string) => {
    setSelectedCategoryIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  };

  // 선택한 카테고리를 기존 클래스에 반영
  const handleApplySelectedCategories = async () => {
    if (!user?.id || !currentClass?.id || selectedCategoryIds.size === 0) return;
    
    try {
      // 선택한 카테고리들 가져오기
      const selectedCategories = sourceCategories.filter(cat => 
        selectedCategoryIds.has(cat.id)
      );
      
      // 기존 카테고리와 이름이 중복되지 않는 것만 추가
      const existingNames = new Set(localCategories.map(c => c.name.toLowerCase()));
      const newCategoriesToAdd = selectedCategories.filter(cat => 
        !existingNames.has(cat.name.toLowerCase())
      );
      
      // 로컬 카테고리에 추가 (임시 ID로 추가)
      const newLocalCategories = [...localCategories];
      newCategoriesToAdd.forEach(cat => {
        const newCat: AssignmentCategory = {
          ...cat,
          id: `cat_${Date.now()}_${Math.random()}`, // 새 임시 ID
        };
        newLocalCategories.push(newCat);
      });
      
      setLocalCategories(newLocalCategories);
      setHasChanges(true);
      
      // 모달 닫기
      handleCloseImportModal();
      
      alert(`${newCategoriesToAdd.length}개의 카테고리가 추가되었습니다. 저장 버튼을 눌러 변경사항을 저장하세요.`);
    } catch (error) {
      console.error('카테고리 반영 실패:', error);
      alert('카테고리를 반영하는데 실패했습니다.');
    }
  };

  // 노트 카테고리용 다른 클래스 데이터 가져오기 모달 열기
  const handleOpenNoteImportModal = async () => {
    if (!user?.id || !currentClass?.id) return;
    
    // 현재 노트 카테고리 수 확인
    const limitCheck = checkNoteCategoryLimit(user, localNoteCategories.length);
    if (!limitCheck.allowed) {
      setLicenseLimitMessage(limitCheck.message || '');
      setShowLicenseLimitModal(true);
      return;
    }
    
    try {
      // 현재 클래스를 제외한 모든 클래스 목록 가져오기
      const allClasses = await userDataService.loadUserClasses(user.id);
      const filteredClasses = allClasses.filter(c => c.id !== currentClass.id);
      setAvailableClassesForNote(filteredClasses);
      setShowNoteImportModal(true);
      setSelectedSourceClassIdForNote('');
      setSourceNoteCategories([]);
      setSelectedNoteCategoryIds(new Set());
    } catch (error) {
      console.error('클래스 목록 로드 실패:', error);
      alert('클래스 목록을 불러오는데 실패했습니다.');
    }
  };

  // 노트 카테고리용 다른 클래스 데이터 가져오기 모달 닫기
  const handleCloseNoteImportModal = () => {
    setShowNoteImportModal(false);
    setSelectedSourceClassIdForNote('');
    setSourceNoteCategories([]);
    setSelectedNoteCategoryIds(new Set());
  };

  // 노트 카테고리용 소스 클래스 선택 시 카테고리 로드
  const handleSourceClassChangeForNote = async (classId: string) => {
    if (!user?.id || !classId) return;
    
    setSelectedSourceClassIdForNote(classId);
    setIsNoteClassDropdownOpen(false);
    setIsLoadingSourceNoteCategories(true);
    setSourceNoteCategories([]);
    setSelectedNoteCategoryIds(new Set());
    
    try {
      const categories = await boardService.getCategories(user.id, classId);
      setSourceNoteCategories(categories);
    } catch (error) {
      console.error('노트 카테고리 로드 실패:', error);
      alert('노트 카테고리를 불러오는데 실패했습니다.');
    } finally {
      setIsLoadingSourceNoteCategories(false);
    }
  };

  // 노트 카테고리 선택/해제 (제한 체크 포함)
  const handleToggleNoteCategorySelection = (categoryId: string) => {
    // 현재 선택하려는 카테고리가 이미 선택되어 있는지 확인
    const isCurrentlySelected = selectedNoteCategoryIds.has(categoryId);
    
    if (isCurrentlySelected) {
      // 해제하는 경우
      setSelectedNoteCategoryIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(categoryId);
        return newSet;
      });
    } else {
      // 선택하는 경우 - 제한 체크
      const limitCheck = checkNoteCategoryLimit(user, localNoteCategories.length + selectedNoteCategoryIds.size + 1);
      if (!limitCheck.allowed) {
        // 토스트 메시지 표시
        setToastMessage('현재 클래스에 생성할 수 있는 노트 카테고리 수를 넘어 선택할 수 없습니다.');
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000);
        return;
      }
      
      setSelectedNoteCategoryIds(prev => {
        const newSet = new Set(prev);
        newSet.add(categoryId);
        return newSet;
      });
    }
  };

  // 선택한 노트 카테고리를 기존 클래스에 반영
  const handleApplySelectedNoteCategories = async () => {
    if (!user?.id || !currentClass?.id || selectedNoteCategoryIds.size === 0) return;
    
    try {
      // 최종 제한 체크
      const limitCheck = checkNoteCategoryLimit(user, localNoteCategories.length + selectedNoteCategoryIds.size);
      if (!limitCheck.allowed) {
        setToastMessage('현재 클래스에 생성할 수 있는 노트 카테고리 수를 넘어 선택할 수 없습니다.');
        setShowToast(true);
        setTimeout(() => {
          setShowToast(false);
        }, 3000);
        return;
      }
      
      // 선택한 카테고리들 가져오기
      const selectedCategories = sourceNoteCategories.filter(cat => 
        selectedNoteCategoryIds.has(cat.id)
      );
      
      // 기존 카테고리와 이름이 중복되지 않는 것만 추가
      const existingNames = new Set(localNoteCategories.map(c => c.name.toLowerCase()));
      const newCategoriesToAdd = selectedCategories.filter(cat => 
        !existingNames.has(cat.name.toLowerCase())
      );
      
      // 로컬 노트 카테고리에 추가 (임시 ID로 추가)
      const newLocalNoteCategories = [...localNoteCategories];
      newCategoriesToAdd.forEach((cat, index) => {
        const newCat: NotebookCategory = {
          ...cat,
          id: `note_cat_${Date.now()}_${index}_${Math.random()}`, // 새 임시 ID
          order: localNoteCategories.length + index,
        };
        newLocalNoteCategories.push(newCat);
      });
      
      setLocalNoteCategories(newLocalNoteCategories);
      setHasNoteCategoryChanges(true);
      
      // 모달 닫기
      handleCloseNoteImportModal();
      
      alert(`${newCategoriesToAdd.length}개의 노트 카테고리가 추가되었습니다. 저장 버튼을 눌러 변경사항을 저장하세요.`);
    } catch (error) {
      console.error('노트 카테고리 반영 실패:', error);
      alert('노트 카테고리를 반영하는데 실패했습니다.');
    }
  };

  // 토스트 메시지 표시
  useEffect(() => {
    if (showToast) {
      const timer = setTimeout(() => {
        setShowToast(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showToast]);

  // 동기화 모달 열기
  const handleOpenSyncModal = async () => {
    // 프로 플랜 또는 관리자만 사용 가능
    if (user.userType !== 'pro' && user.userType !== 'admin') {
      setLicenseLimitMessage('클래스 연동 설정은 프로 플랜에서 사용할 수 있습니다.');
      setShowLicenseLimitModal(true);
      return;
    }
    
    if (!user?.id || !currentClass?.id) return;
    
    setShowSyncModal(true);
    setSyncSelectedClassId('');
    
    try {
      const allClasses = await userDataService.loadUserClasses(user.id);
      const filteredClasses = allClasses.filter(c => c.id !== currentClass.id);
      setSyncAvailableClasses(filteredClasses);
    } catch (error) {
      console.error('클래스 목록 로드 실패:', error);
      setSyncAvailableClasses([]);
    }
  };

  // 동기화 모달 닫기
  const handleCloseSyncModal = () => {
    setShowSyncModal(false);
    setSyncSelectedClassId('');
    setIsSyncClassDropdownOpen(false);
  };

  // 동기화 확인 모달 열기
  const handleOpenSyncConfirm = () => {
    if (!syncSelectedClassId) {
      alert('동기화할 클래스를 선택해주세요.');
      return;
    }
    setShowSyncConfirmModal(true);
  };

  // 동기화 실행
  const handleSyncSettings = async () => {
    if (!user?.id || !currentClass?.id || !syncSelectedClassId) return;
    
    setIsSyncing(true);
    setShowSyncConfirmModal(false);
    
    try {
      // 1. 기본 환경설정 동기화
      // 과제 카테고리
      const sourceCategories = await assignmentCategoryService.loadCategories(user.id, syncSelectedClassId);
      // 과제 카테고리 일괄 저장
      await defaultSetService.updateDefaultSet(user.id, currentClass.id, {
        assignmentCategories: sourceCategories
      });
      
      // 노트 카테고리
      const sourceNoteCategories = await boardService.getCategories(user.id, syncSelectedClassId);
      // 노트 카테고리 일괄 저장 (Firestore에 직접 저장)
      const noteBookDocRef = doc(db, `users/${user.id}/classes/${currentClass.id}/setCollection/NoteBook`);
      await setDoc(noteBookDocRef, {
        categories: sourceNoteCategories.map(cat => ({
          id: cat.id,
          name: cat.name,
          order: cat.order,
          createdAt: cat.createdAt // NotebookCategory의 createdAt은 이미 Timestamp 타입
        })),
        updatedAt: Timestamp.now()
      }, { merge: true });
      
      // 기본 포인트 설정 (defaultSetService에서 로드)
      const sourceDefaultSet = await defaultSetService.loadDefaultSet(user.id, syncSelectedClassId);
      if (sourceDefaultSet?.assignmentSettings) {
        await defaultSetService.saveAssignmentSettings(user.id, currentClass.id, {
          defaultPoints: sourceDefaultSet.assignmentSettings.defaultPoints || 10,
          statusDefaultPoints: sourceDefaultSet.assignmentSettings.statusDefaultPoints || {
            pending: 0,
            confirmed: 0,
            delayed: 0,
            exempted: 0
          },
          updatedAt: new Date()
        });
      }
      if (sourceDefaultSet?.coinSettings) {
        await defaultSetService.saveCoinSettings(user.id, currentClass.id, {
          defaultPoints: sourceDefaultSet.coinSettings.defaultPoints || 10
        });
      }
      
      // 2. 포인트 세트 설정 동기화
      const sourcePointService = new PointTypeManagementService(user.id, syncSelectedClassId);
      const sourcePointData = await sourcePointService.getPointTypeData();
      // 포인트 타입 데이터 일괄 저장 (Firestore에 직접 저장)
      const currentPointDocRef = doc(db, `users/${user.id}/classes/${currentClass.id}/setCollection/pointLevelSet`);
      
      const convertDateToTimestamp = (date: Date | any): Timestamp => {
        if (date instanceof Date) {
          return Timestamp.fromDate(date);
        }
        if (date instanceof Timestamp) {
          return date;
        }
        if (date && typeof date.toDate === 'function') {
          return Timestamp.fromDate(date.toDate());
        }
        return Timestamp.now();
      };
      
      await setDoc(currentPointDocRef, {
        positiveTypes: sourcePointData.positiveTypes.map(type => ({
          ...type,
          createdAt: convertDateToTimestamp(type.createdAt),
          updatedAt: convertDateToTimestamp(type.updatedAt)
        })),
        negativeTypes: sourcePointData.negativeTypes.map(type => ({
          ...type,
          createdAt: convertDateToTimestamp(type.createdAt),
          updatedAt: convertDateToTimestamp(type.updatedAt)
        })),
        lastUpdated: Timestamp.now()
      }, { merge: true });
      
      // 3. 아이템 카드 설정 동기화
      const sourceItemService = new ItemCardService(user.id, syncSelectedClassId);
      const sourceItemData = await sourceItemService.loadItemCardSet();
      if (sourceItemData) {
        const currentItemDocRef = doc(db, `users/${user.id}/classes/${currentClass.id}/setCollection/itemCardSet`);
        
        const cleanItemForFirestore = (item: any) => {
          const firestoreItem: any = {
            itemCardId: item.itemCardId,
            name: item.name,
            memo: item.memo,
            type: item.type,
            cost: item.cost,
            category: item.category,
            createdAt: item.createdAt instanceof Date ? Timestamp.fromDate(item.createdAt) : (item.createdAt?.toDate ? Timestamp.fromDate(item.createdAt.toDate()) : Timestamp.now()),
            updatedAt: item.updatedAt instanceof Date ? Timestamp.fromDate(item.updatedAt) : (item.updatedAt?.toDate ? Timestamp.fromDate(item.updatedAt.toDate()) : Timestamp.now())
          };
          
          if (item.useCount !== undefined) {
            firestoreItem.useCount = item.useCount;
          }
          
          if (item.icon !== undefined && item.icon !== null) {
            firestoreItem.icon = item.icon;
          }
          
          if (item.cardImage !== undefined && item.cardImage !== null) {
            firestoreItem.cardImage = item.cardImage;
          }
          
          return firestoreItem;
        };
        
        await setDoc(currentItemDocRef, {
          levelupItem: (sourceItemData.levelupItem || []).map(cleanItemForFirestore),
          assignmentItem: (sourceItemData.assignmentItem || []).map(cleanItemForFirestore),
          storeItem: (sourceItemData.storeItem || []).map(cleanItemForFirestore),
          userCardImageOrder: sourceItemData.userCardImageOrder || [],
          updatedAt: Timestamp.now()
        }, { merge: true });
      }
      
      // 4. 레벨 시스템 설정 동기화
      const sourceLevelSetRef = doc(db, 'users', user.id, 'classes', syncSelectedClassId, 'setCollection', 'pointLevelSet');
      const sourceLevelDoc = await getDoc(sourceLevelSetRef);
      if (sourceLevelDoc.exists()) {
        const sourceLevelData = sourceLevelDoc.data();
        const currentLevelSetRef = doc(db, 'users', user.id, 'classes', currentClass.id, 'setCollection', 'pointLevelSet');
        await setDoc(currentLevelSetRef, {
          levelUpPoints: sourceLevelData.levelUpPoints || 1,
          selectedLevel: sourceLevelData.selectedLevel || 1,
          levelCoins: sourceLevelData.levelCoins || {},
          levelItemCards: sourceLevelData.levelItemCards || {},
          updatedAt: new Date()
        }, { merge: true });
      }
      
      // 5. 타임태그 설정 동기화
      const sourceTimeTags = await TimeTagService.getTimeTags(user.id, syncSelectedClassId);
      await TimeTagService.saveTimeTags(user.id, currentClass.id, sourceTimeTags);
      
      // 로컬 상태 업데이트
      setCategories(sourceCategories);
      setLocalCategories([...sourceCategories]);
      setNoteCategories(sourceNoteCategories);
      setLocalNoteCategories([...sourceNoteCategories]);
      setHasChanges(false);
      setHasNoteCategoryChanges(false);
      
      if (sourceDefaultSet?.assignmentSettings) {
        setDefaultPoints(sourceDefaultSet.assignmentSettings.defaultPoints || 10);
        setLocalDefaultPoints(sourceDefaultSet.assignmentSettings.defaultPoints || 10);
        setStatusDefaultPoints(sourceDefaultSet.assignmentSettings.statusDefaultPoints || {
          pending: 0,
          confirmed: 0,
          delayed: 0,
          exempted: 0
        });
        const statusPoints = sourceDefaultSet.assignmentSettings.statusDefaultPoints || {
          pending: 0,
          confirmed: 0,
          delayed: 0,
          exempted: 0
        };
        setLocalStatusDefaultPoints(statusPoints);
        setDelayedPercentDisplay(String(statusPoints.delayed ?? 0));
        setExemptedPercentDisplay(String(statusPoints.exempted ?? 0));
        setHasPointChanges(false);
      }
      if (sourceDefaultSet?.coinSettings) {
        setCoinDefaultPoints(sourceDefaultSet.coinSettings.defaultPoints || 10);
        setLocalCoinDefaultPoints(sourceDefaultSet.coinSettings.defaultPoints || 10);
        setHasCoinChanges(false);
      }
      
      handleCloseSyncModal();
      alert('클래스 설정이 성공적으로 동기화되었습니다.');
      
      // 콜백 호출하여 상위 컴포넌트에 변경사항 알림
      if (onCategoriesChange) {
        onCategoriesChange(sourceCategories);
      }
      if (onDefaultPointsChange && sourceDefaultSet?.assignmentSettings) {
        onDefaultPointsChange(sourceDefaultSet.assignmentSettings.defaultPoints || 10);
      }
      if (onStatusDefaultPointsChange && sourceDefaultSet?.assignmentSettings) {
        onStatusDefaultPointsChange(sourceDefaultSet.assignmentSettings.statusDefaultPoints || {
          pending: 0,
          confirmed: 0,
          delayed: 0,
          exempted: 0
        });
      }
      if (onCoinSettingsChange && sourceDefaultSet?.coinSettings) {
        onCoinSettingsChange(sourceDefaultSet.coinSettings.defaultPoints || 10);
      }
    } catch (error) {
      console.error('설정 동기화 실패:', error);
      alert('설정 동기화 중 오류가 발생했습니다.');
    } finally {
      setIsSyncing(false);
    }
  };

  const handleCancel = () => {
    // 변경사항이 있으면 확인 모달 표시
    if (hasChanges || hasPointChanges || hasCoinChanges || hasNoteCategoryChanges || hasIndividualAccessChanges || hasIndividualRecordViewChanges || hasGuestEntryChanges) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <>
      {/* 취소 확인 모달 - 가장 위에 렌더링 */}
      {showCancelConfirm && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showCancelConfirm}
            onClose={() => setShowCancelConfirm(false)}
            onConfirm={() => {
              setShowCancelConfirm(false);
              onClose();
            }}
            onCancel={() => {
              setShowCancelConfirm(false);
              saveChanges();
            }}
            title={[
              "변경사항이 있습니다.",
              "저장하지 않고 나가시겠습니까?",
              "변경사항이 모두 사라집니다."
            ]}
            confirmText="나가기"
            cancelText="저장"
            icon="!"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}

      {/* 카테고리 삭제 확인 모달 */}
      {showDeleteCategoryConfirm && categoryToDelete && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showDeleteCategoryConfirm}
            onClose={cancelDeleteCategory}
            onConfirm={confirmDeleteCategory}
            onCancel={cancelDeleteCategory}
            title={[
              `"${categoryToDelete.name}" 카테고리를 삭제하시겠습니까?`,
              `이 카테고리에는 ${categoryNotesCount}개의 노트가 있습니다.`,
              "삭제하면 되돌릴 수 없습니다."
            ]}
            confirmText="삭제"
            cancelText="취소"
            icon="⚠️"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}

      {/* 노트북 잠금 비밀번호 설정 모달 (잠금 시) */}
      {showLockPasswordModal && lockPasswordCategory && (
        <NotebookLockPasswordModalOverlay onClick={() => setShowLockPasswordModal(false)}>
          <NotebookLockPasswordModalContent onClick={(e) => e.stopPropagation()}>
            <NotebookLockPasswordModalTitle>잠금 비밀번호 설정</NotebookLockPasswordModalTitle>
            <NotebookLockPasswordModalDesc>
              &quot;{lockPasswordCategory.name}&quot; 카테고리를 잠그려면 잠금 비밀번호를 설정해 주세요. (4자 이상)
            </NotebookLockPasswordModalDesc>
            <NotebookLockPasswordModalInput
              type="password"
              value={lockPasswordValue}
              onChange={(e) => { setLockPasswordValue(e.target.value); setLockPasswordError(''); }}
              placeholder="잠금 비밀번호"
              autoComplete="new-password"
            />
            <NotebookLockPasswordModalInput
              type="password"
              value={lockPasswordConfirm}
              onChange={(e) => { setLockPasswordConfirm(e.target.value); setLockPasswordError(''); }}
              placeholder="비밀번호 확인"
              autoComplete="new-password"
            />
            {lockPasswordError ? <NotebookLockPasswordModalError>{lockPasswordError}</NotebookLockPasswordModalError> : null}
            <NotebookLockPasswordModalButtons>
              <NotebookLockPasswordModalButton type="button" onClick={() => setShowLockPasswordModal(false)}>
                취소
              </NotebookLockPasswordModalButton>
              <NotebookLockPasswordModalButton $primary type="button" onClick={confirmLockPassword}>
                설정
              </NotebookLockPasswordModalButton>
            </NotebookLockPasswordModalButtons>
          </NotebookLockPasswordModalContent>
        </NotebookLockPasswordModalOverlay>
      )}

      {/* 노트북 잠금 해제 - 계정 비밀번호 입력 모달 */}
      {showUnlockConfirmModal && unlockCategory && (
        <NotebookLockPasswordModalOverlay onClick={() => setShowUnlockConfirmModal(false)}>
          <NotebookLockPasswordModalContent onClick={(e) => e.stopPropagation()}>
            <NotebookLockPasswordModalTitle>계정 암호 입력</NotebookLockPasswordModalTitle>
            <NotebookLockPasswordModalDesc>
              &quot;{unlockCategory.name}&quot; 카테고리 잠금을 해제하려면 계정 암호를 입력해 주세요.
            </NotebookLockPasswordModalDesc>
            <NotebookLockPasswordModalInput
              type="password"
              value={unlockAccountPassword}
              onChange={(e) => { setUnlockAccountPassword(e.target.value); setUnlockError(''); }}
              placeholder="계정 비밀번호"
              autoComplete="current-password"
              onKeyDown={(e) => e.key === 'Enter' && confirmUnlockWithAccountPassword()}
            />
            {unlockError ? <NotebookLockPasswordModalError>{unlockError}</NotebookLockPasswordModalError> : null}
            <NotebookLockPasswordModalButtons>
              <NotebookLockPasswordModalButton type="button" onClick={() => setShowUnlockConfirmModal(false)}>
                취소
              </NotebookLockPasswordModalButton>
              <NotebookLockPasswordModalButton $primary type="button" onClick={confirmUnlockWithAccountPassword}>
                확인
              </NotebookLockPasswordModalButton>
            </NotebookLockPasswordModalButtons>
          </NotebookLockPasswordModalContent>
        </NotebookLockPasswordModalOverlay>
      )}

      {/* 개별 기록 열람 잠금 비밀번호 설정 모달 */}
      {showRecordLockPasswordModal && (
        <NotebookLockPasswordModalOverlay onClick={() => setShowRecordLockPasswordModal(false)}>
          <NotebookLockPasswordModalContent onClick={(e) => e.stopPropagation()}>
            <NotebookLockPasswordModalTitle>잠금 비밀번호 설정</NotebookLockPasswordModalTitle>
            <NotebookLockPasswordModalDesc>
              개별 학생 기록 열람에 잠금을 걸려면 잠금 비밀번호를 설정해 주세요. (4자 이상)
            </NotebookLockPasswordModalDesc>
            <NotebookLockPasswordModalInput
              type="password"
              value={recordLockPasswordValue}
              onChange={(e) => { setRecordLockPasswordValue(e.target.value); setRecordLockPasswordError(''); }}
              placeholder="잠금 비밀번호"
              autoComplete="new-password"
            />
            <NotebookLockPasswordModalInput
              type="password"
              value={recordLockPasswordConfirm}
              onChange={(e) => { setRecordLockPasswordConfirm(e.target.value); setRecordLockPasswordError(''); }}
              placeholder="비밀번호 확인"
              autoComplete="new-password"
            />
            {recordLockPasswordError ? <NotebookLockPasswordModalError>{recordLockPasswordError}</NotebookLockPasswordModalError> : null}
            <NotebookLockPasswordModalButtons>
              <NotebookLockPasswordModalButton type="button" onClick={() => setShowRecordLockPasswordModal(false)}>
                취소
              </NotebookLockPasswordModalButton>
              <NotebookLockPasswordModalButton $primary type="button" onClick={confirmRecordLockPassword}>
                설정
              </NotebookLockPasswordModalButton>
            </NotebookLockPasswordModalButtons>
          </NotebookLockPasswordModalContent>
        </NotebookLockPasswordModalOverlay>
      )}

      {/* 개별 기록 열람 잠금 해제 - 계정 비밀번호 입력 모달 */}
      {showRecordUnlockModal && (
        <NotebookLockPasswordModalOverlay onClick={() => setShowRecordUnlockModal(false)}>
          <NotebookLockPasswordModalContent onClick={(e) => e.stopPropagation()}>
            <NotebookLockPasswordModalTitle>계정 암호 입력</NotebookLockPasswordModalTitle>
            <NotebookLockPasswordModalDesc>
              개별 학생 기록 열람 잠금을 해제하려면 계정 암호를 입력해 주세요.
            </NotebookLockPasswordModalDesc>
            <NotebookLockPasswordModalInput
              type="password"
              value={recordUnlockAccountPassword}
              onChange={(e) => { setRecordUnlockAccountPassword(e.target.value); setRecordUnlockError(''); }}
              placeholder="계정 비밀번호"
              autoComplete="current-password"
              onKeyDown={(e) => e.key === 'Enter' && confirmRecordUnlockWithAccountPassword()}
            />
            {recordUnlockError ? <NotebookLockPasswordModalError>{recordUnlockError}</NotebookLockPasswordModalError> : null}
            <NotebookLockPasswordModalButtons>
              <NotebookLockPasswordModalButton type="button" onClick={() => setShowRecordUnlockModal(false)}>
                취소
              </NotebookLockPasswordModalButton>
              <NotebookLockPasswordModalButton $primary type="button" onClick={confirmRecordUnlockWithAccountPassword}>
                확인
              </NotebookLockPasswordModalButton>
            </NotebookLockPasswordModalButtons>
          </NotebookLockPasswordModalContent>
        </NotebookLockPasswordModalOverlay>
      )}

      <Overlay onClick={handleCancel} />
      <Wrapper>
        <Content onClick={(e) => e.stopPropagation()}>
          {/* 색상 선택 팝업 외부 클릭 시 닫기 */}
          {colorPickerOpen && (
            <div 
              style={{ position: 'fixed', inset: 0, zIndex: 998 }} 
              onClick={() => setColorPickerOpen(null)} 
            />
          )}
          
          <HeaderSection>
            <Title>기본 환경설정</Title>
          </HeaderSection>
          
          <ScrollableContent>
            {/* 타임 태그 설정 섹션 (최상단) */}
            {user && currentClass && (
              <CategorySection>
                <SectionTitle>타임 태그 설정</SectionTitle>
                <TimeTagSettingsModal
                  isOpen={true}
                  renderAsSection
                  onClose={() => {}}
                  onSave={() => {}}
                  initialTimeTags={localTimeTags}
                  alarmSets={alarmSets}
                  onCreateAlarms={onCreateAlarms}
                  userId={user.id}
                  classId={currentClass.id}
                  onTimeTagsChange={handleTimeTagsChange}
                />
              </CategorySection>
            )}

            <Divider />

            <PointsSection>
            <SectionTitle>기본 단위 설정</SectionTitle>
            <PointsRow>
              <PointsLabel>기본 단위 포인트</PointsLabel>
              <PointsButtonGroup>
                <PointsButton 
                  $isSelected={localDefaultPoints === 1}
                  onClick={() => handleDefaultPointsChange(1)}
                >
                  1점
                </PointsButton>
                <PointsButton 
                  $isSelected={localDefaultPoints === 10}
                  onClick={() => handleDefaultPointsChange(10)}
                >
                  10점
                </PointsButton>
                <PointsButton 
                  $isSelected={localDefaultPoints === 100}
                  onClick={() => handleDefaultPointsChange(100)}
                >
                  100점
                </PointsButton>
              </PointsButtonGroup>
            </PointsRow>

            <PointsRow style={{ marginTop: '24px' }}>
              <PointsLabel>기본 단위 코인</PointsLabel>
              <PointsButtonGroup>
                <PointsButton 
                  $isSelected={localCoinDefaultPoints === 1}
                  onClick={() => handleCoinDefaultPointsChange(1)}
                >
                  1코인
                </PointsButton>
                <PointsButton 
                  $isSelected={localCoinDefaultPoints === 10}
                  onClick={() => handleCoinDefaultPointsChange(10)}
                >
                  10코인
                </PointsButton>
                <PointsButton 
                  $isSelected={localCoinDefaultPoints === 100}
                  onClick={() => handleCoinDefaultPointsChange(100)}
                >
                  100코인
                </PointsButton>
              </PointsButtonGroup>
            </PointsRow>
            
            <StatusPointsSection>
              <StatusPointsTitle>과제 상태별 기본 지급 포인트 설정</StatusPointsTitle>
              <StatusPointsGrid>
                <StatusPointItem>
                  <StatusPointBox>
                    <StatusPointHeader>
                      <StatusPointColorDot style={{ backgroundColor: 'var(--status-pending)' }}>
                        1
                      </StatusPointColorDot>
                      <StatusPointLabel>미확인</StatusPointLabel>
                    </StatusPointHeader>
                    <StatusPointText>포인트 미지급</StatusPointText>
                  </StatusPointBox>
                </StatusPointItem>
                
                <StatusPointItem>
                  <StatusPointBox>
                    <StatusPointHeader>
                      <StatusPointColorDot style={{ backgroundColor: '#227971' }}>
                        2
                      </StatusPointColorDot>
                      <StatusPointLabel>확인</StatusPointLabel>
                    </StatusPointHeader>
                    <StatusPointText>과제별 설정</StatusPointText>
                  </StatusPointBox>
                </StatusPointItem>
                
                <StatusPointItem>
                  <StatusPointBox>
                    <StatusPointHeader>
                      <StatusPointColorDot style={{ backgroundColor: 'var(--status-delayed)' }}>
                        3
                      </StatusPointColorDot>
                      <StatusPointLabel>지연확인 (%)</StatusPointLabel>
                    </StatusPointHeader>
                    <StatusPointInput
                      type="number"
                      min={0}
                      max={100}
                      value={delayedPercentDisplay}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDelayedPercentDisplay(v);
                        const num = v === '' ? 0 : Number(v) || 0;
                        setLocalStatusDefaultPoints(prev => ({
                          ...prev,
                          delayed: Math.min(100, Math.max(0, num))
                        }));
                        setHasPointChanges(true);
                      }}
                      onBlur={() => {
                        if (delayedPercentDisplay === '') {
                          setDelayedPercentDisplay('0');
                          setLocalStatusDefaultPoints(prev => ({ ...prev, delayed: 0 }));
                        } else {
                          const n = Number(delayedPercentDisplay);
                          if (!isNaN(n)) {
                            const clamped = Math.min(100, Math.max(0, n));
                            setDelayedPercentDisplay(String(clamped));
                            setLocalStatusDefaultPoints(prev => ({ ...prev, delayed: clamped }));
                          }
                        }
                      }}
                    />
                    <StatusPointHint>확인 포인트의 n% 지급 (반올림)</StatusPointHint>
                  </StatusPointBox>
                </StatusPointItem>
                
                <StatusPointItem>
                  <StatusPointBox>
                    <StatusPointHeader>
                      <StatusPointColorDot style={{ backgroundColor: 'var(--status-exempted)' }}>
                        4
                      </StatusPointColorDot>
                      <StatusPointLabel>예외 (%)</StatusPointLabel>
                    </StatusPointHeader>
                    <StatusPointInput
                      type="number"
                      min={0}
                      max={100}
                      value={exemptedPercentDisplay}
                      onChange={(e) => {
                        const v = e.target.value;
                        setExemptedPercentDisplay(v);
                        const num = v === '' ? 0 : Number(v) || 0;
                        setLocalStatusDefaultPoints(prev => ({
                          ...prev,
                          exempted: Math.min(100, Math.max(0, num))
                        }));
                        setHasPointChanges(true);
                      }}
                      onBlur={() => {
                        if (exemptedPercentDisplay === '') {
                          setExemptedPercentDisplay('0');
                          setLocalStatusDefaultPoints(prev => ({ ...prev, exempted: 0 }));
                        } else {
                          const n = Number(exemptedPercentDisplay);
                          if (!isNaN(n)) {
                            const clamped = Math.min(100, Math.max(0, n));
                            setExemptedPercentDisplay(String(clamped));
                            setLocalStatusDefaultPoints(prev => ({ ...prev, exempted: clamped }));
                          }
                        }
                      }}
                    />
                    <StatusPointHint>확인 포인트의 n% 지급 (반올림)</StatusPointHint>
                  </StatusPointBox>
                </StatusPointItem>
              </StatusPointsGrid>
            </StatusPointsSection>
          </PointsSection>
            
            <Divider />
            
            <CategorySection>
              <SectionTitle>과제 카테고리 설정</SectionTitle>
              
              <CreateSection>
                <CreateTitle>과제 카테고리 생성</CreateTitle>
                <Row>
                  <Input
                    value={newName}
                    onChange={e => setNewName(e.target.value)}
                    placeholder="새 카테고리 이름"
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCategory(); } }}
                  />
                  <ColorSelector>
                    <ColorDisplay 
                      $color={newColor} 
                      onClick={(e) => {
                        const rect = e.currentTarget.getBoundingClientRect();
                        const popupWidth = 140;
                        const popupHeight = 80;
                        
                        let x = rect.left - popupWidth - 10;
                        let y = rect.top + rect.height / 2 - popupHeight / 2;
                        
                        if (x < 20) {
                          x = rect.right + 10;
                        }
                        if (y < 20) {
                          y = rect.bottom + 10;
                        }
                        if (y + popupHeight > window.innerHeight - 20) {
                          y = rect.top - popupHeight - 10;
                        }
                        
                        setPopupPosition({ x, y });
                        setColorPickerOpen('new');
                      }}
                      title="색상 선택"
                    />
                    {colorPickerOpen === 'new' && (
                      <ColorPickerPopup style={{ left: popupPosition.x, top: popupPosition.y }}>
                        {DEFAULT_COLORS.map(color => (
                          <ColorOption
                            key={color}
                            $color={color}
                            $isSelected={newColor === color}
                            onClick={() => {
                              setNewColor(color);
                              setColorPickerOpen(null);
                            }}
                            title={color}
                          />
                        ))}
                      </ColorPickerPopup>
                    )}
                  </ColorSelector>
                  <PrimaryButton type="button" onClick={() => addCategory()} title="추가">+</PrimaryButton>
                </Row>
              </CreateSection>
              
              <ListSection>
                <ListTitle>생성된 과제 카테고리</ListTitle>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event: DragEndEvent) => {
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;
                    const oldIndex = localCategories.findIndex(cat => cat.id === active.id);
                    const newIndex = localCategories.findIndex(cat => cat.id === over.id);
                    if (oldIndex === -1 || newIndex === -1) return;
                    const newOrder = arrayMove(localCategories, oldIndex, newIndex);
                    setLocalCategories(newOrder);
                    setHasChanges(true);
                    if (onCategoriesChange) {
                      onCategoriesChange(newOrder);
                    }
                  }}
                >
                  <SortableContext items={localCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    <List>
                      {localCategories.map(cat => (
                        <SortableAssignmentCategoryRow
                          key={cat.id}
                          cat={cat}
                          colorPickerOpen={colorPickerOpen}
                          popupPosition={popupPosition}
                          renameCategory={renameCategory}
                          openColorPicker={openColorPicker}
                          updateCategoryColor={updateCategoryColor}
                          deleteCategory={deleteCategory}
                        />
                      ))}
                    </List>
                  </SortableContext>
                </DndContext>
              </ListSection>
              
              {/* 다른 클래스 데이터 가져오기 버튼 */}
              {isImportButtonEnabled && (
                <ImportButton onClick={handleOpenImportModal}>
                  다른 클래스 데이터 가져오기
                </ImportButton>
              )}
            </CategorySection>

            <Divider />

            {/* 노트 카테고리 설정 섹션 */}
            <CategorySection>
              <SectionTitle>노트북 카테고리 설정</SectionTitle>
              
              <CreateSection>
                <CreateTitle>노트북 생성</CreateTitle>
                <NoteCategoryRow>
                  <Input 
                    value={newNoteCategoryName} 
                    onChange={e => setNewNoteCategoryName(e.target.value)} 
                    placeholder="새 카테고리 이름"
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        addNoteCategory();
                      }
                    }}
                  />
                  <PrimaryButton onClick={addNoteCategory} title="추가">+</PrimaryButton>
                </NoteCategoryRow>
              </CreateSection>
              
              <ListSection>
                <ListTitle>생성된 노트북</ListTitle>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={(event: DragEndEvent) => {
                    const { active, over } = event;
                    if (!over || active.id === over.id) return;
                    const oldIndex = localNoteCategories.findIndex(cat => cat.id === active.id);
                    const newIndex = localNoteCategories.findIndex(cat => cat.id === over.id);
                    if (oldIndex === -1 || newIndex === -1) return;
                    const newOrder = arrayMove(localNoteCategories, oldIndex, newIndex);
                    setLocalNoteCategories(newOrder);
                    setHasNoteCategoryChanges(true);
                  }}
                >
                  <SortableContext items={localNoteCategories.map(c => c.id)} strategy={verticalListSortingStrategy}>
                    <List>
                      {localNoteCategories.map(cat => (
                        <SortableNoteCategoryRow
                          key={cat.id}
                          cat={cat}
                          renameNoteCategory={renameNoteCategory}
                          deleteNoteCategory={deleteNoteCategory}
                          toggleLockNoteCategory={toggleLockNoteCategory}
                        />
                      ))}
                    </List>
                  </SortableContext>
                </DndContext>
              </ListSection>
              
              {/* 다른 클래스 데이터 가져오기 버튼 */}
              {isImportButtonEnabled && (
                <ImportButton onClick={handleOpenNoteImportModal}>
                  다른 클래스 데이터 가져오기
                </ImportButton>
              )}
            </CategorySection>

            <Divider />

            {/* 개별 기록 설정 섹션 */}
            <CategorySection>
              <SectionTitle>개별 기록 설정</SectionTitle>
              <NoteCategoryRow style={{ alignItems: 'center', gap: 8 }}>
                <span style={{ flex: 1 }}>포인트 코인 기록 조회 페이지의 개별 학생블럭 기록 열람</span>
                <LockButtonTooltipWrapper data-tooltip={localIndividualRecordViewLocked ? '잠금 해제' : '잠금'}>
                  <LockButton
                    type="button"
                    $locked={localIndividualRecordViewLocked}
                    onClick={toggleRecordViewLock}
                    title={localIndividualRecordViewLocked ? '잠금 해제' : '잠금'}
                    aria-label={localIndividualRecordViewLocked ? '잠금 해제' : '잠금'}
                  >
                    {localIndividualRecordViewLocked ? (
                      <img src="/lock.svg" alt="잠금" width={14} height={14} />
                    ) : (
                      <img src="/unlock.svg" alt="잠금 해제" width={14} height={14} />
                    )}
                  </LockButton>
                </LockButtonTooltipWrapper>
              </NoteCategoryRow>
            </CategorySection>

            <Divider />
            
            {/* 클래스 연동 설정 섹션 */}
            <CategorySection>
              <SectionTitle>클래스 연동 설정</SectionTitle>
              
              <SyncSettingRow>
                <SyncSettingLabel>
                  <span>다른 클래스 데이터 가져오기 버튼 활성화</span>
                </SyncSettingLabel>
                <ToggleSwitch
                  $isOn={isImportButtonEnabled}
                  $disabled={user.userType !== 'pro' && user.userType !== 'admin'}
                  onClick={() => {
                    // 프로 플랜 또는 관리자만 사용 가능
                    if (user.userType !== 'pro' && user.userType !== 'admin') {
                      setLicenseLimitMessage('클래스 연동 설정은 프로 플랜에서 사용할 수 있습니다.');
                      setShowLicenseLimitModal(true);
                      return;
                    }
                    
                    const newValue = !isImportButtonEnabled;
                    setIsImportButtonEnabled(newValue);
                    localStorage.setItem('isImportButtonEnabled', String(newValue));
                    // 같은 탭에서의 변경을 감지하기 위해 커스텀 이벤트 발생
                    window.dispatchEvent(new Event('importButtonEnabledChanged'));
                  }}
                >
                  <ToggleSlider $isOn={!isImportButtonEnabled} $disabled={user.userType !== 'pro' && user.userType !== 'admin'}>
                    OFF
                  </ToggleSlider>
                  <ToggleSlider $isOn={isImportButtonEnabled} $disabled={user.userType !== 'pro' && user.userType !== 'admin'}>
                    ON
                  </ToggleSlider>
                </ToggleSwitch>
              </SyncSettingRow>
              
              {isImportButtonEnabled && (
                <SyncButton onClick={handleOpenSyncModal}>
                  현재 클래스 설정을 다른 클래스 설정과 동기화
                </SyncButton>
              )}
            </CategorySection>

            <Divider />

            {/* 멤버 개별 접근 페이지 설정 섹션 */}
            <CategorySection>
              <SectionTitle>멤버 개별 접근 페이지 설정</SectionTitle>
              
              <SyncSettingRow>
                <SyncSettingLabel>
                  <span>오늘의 루틴 과제 섹션 활성화</span>
                </SyncSettingLabel>
                <ToggleSwitch
                  $isOn={routineSectionEnabled}
                  onClick={() => {
                    const newValue = !routineSectionEnabled;
                    setRoutineSectionEnabled(newValue);
                    setHasIndividualAccessChanges(true);
                  }}
                >
                  <ToggleSlider $isOn={!routineSectionEnabled}>
                    OFF
                  </ToggleSlider>
                  <ToggleSlider $isOn={routineSectionEnabled}>
                    ON
                  </ToggleSlider>
                </ToggleSwitch>
              </SyncSettingRow>

              <SyncSettingRow>
                <SyncSettingLabel>
                  <span>스토어 개별 아이템 구매 활성화</span>
                </SyncSettingLabel>
                <ToggleSwitch
                  $isOn={storeIndividualPurchaseEnabled}
                  onClick={() => {
                    const newValue = !storeIndividualPurchaseEnabled;
                    setStoreIndividualPurchaseEnabled(newValue);
                    setHasIndividualAccessChanges(true);
                  }}
                >
                  <ToggleSlider $isOn={!storeIndividualPurchaseEnabled}>
                    OFF
                  </ToggleSlider>
                  <ToggleSlider $isOn={storeIndividualPurchaseEnabled}>
                    ON
                  </ToggleSlider>
                </ToggleSwitch>
              </SyncSettingRow>
            </CategorySection>

            <Divider />

            {/* 클래스챗 게스트 입장 설정 섹션 */}
            <GuestEntryCategorySection>
              <SectionTitle>클래스챗 게스트 입장 설정</SectionTitle>

              <SyncSettingRow>
                <SyncSettingLabel>
                  <span>클래스챗 게스트 입장</span>
                </SyncSettingLabel>
                <ToggleSwitch
                  $isOn={localGuestEntryEnabled}
                  $disabled={user.userType !== 'standard' && user.userType !== 'pro' && user.userType !== 'admin'}
                  onClick={() => {
                    if (user.userType !== 'standard' && user.userType !== 'pro' && user.userType !== 'admin') {
                      setLicenseLimitMessage('게스트 모드는 스탠다드/프로 플랜에서 사용할 수 있습니다.');
                      setShowLicenseLimitModal(true);
                      return;
                    }
                    const newValue = !localGuestEntryEnabled;
                    setLocalGuestEntryEnabled(newValue);
                    setHasGuestEntryChanges(newValue !== guestEntryEnabled);
                  }}
                >
                  <ToggleSlider $isOn={!localGuestEntryEnabled} $disabled={user.userType !== 'standard' && user.userType !== 'pro' && user.userType !== 'admin'}>
                    OFF
                  </ToggleSlider>
                  <ToggleSlider $isOn={localGuestEntryEnabled} $disabled={user.userType !== 'standard' && user.userType !== 'pro' && user.userType !== 'admin'}>
                    ON
                  </ToggleSlider>
                </ToggleSwitch>
              </SyncSettingRow>
            </GuestEntryCategorySection>
          </ScrollableContent>
          
          <FooterSection>
            <ButtonGroup>
              <Button data-variant="modal-secondary" onClick={handleCancel}>닫기</Button>
              <SaveButton
                onClick={saveChanges}
                disabled={isSaving}
              >
                {isSaving ? '저장 중...' : '저장'}
              </SaveButton>
            </ButtonGroup>
          </FooterSection>
        </Content>
      </Wrapper>
      
      {/* 라이센스 제한 모달 */}
      <LicenseLimitModal
        isOpen={showLicenseLimitModal}
        message={licenseLimitMessage}
        onClose={() => setShowLicenseLimitModal(false)}
        onUpgrade={() => {
          setShowLicenseLimitModal(false);
          onLicenseClick?.();
        }}
      />
      
      {/* 다른 클래스 데이터 가져오기 모달 */}
      {showImportModal && (
        <ImportModalOverlay onClick={handleCloseImportModal}>
          <ImportModalContent onClick={(e) => e.stopPropagation()}>
            <ImportModalHeader>
              <ImportModalTitle>다른 클래스 데이터 가져오기</ImportModalTitle>
              <ImportModalCloseButton onClick={handleCloseImportModal}>×</ImportModalCloseButton>
            </ImportModalHeader>
            
            <ImportModalBody>
              {/* 클래스 선택 드롭다운 */}
              <ImportSection>
                <ImportLabel>클래스 선택</ImportLabel>
                <ImportClassSelectContainer>
                  <ImportClassSelectButton
                    onClick={() => setIsClassDropdownOpen(!isClassDropdownOpen)}
                    type="button"
                  >
                    <ImportClassSelectText>
                      {selectedSourceClassId 
                        ? availableClasses.find(c => c.id === selectedSourceClassId)?.name || '클래스를 선택하세요'
                        : '클래스를 선택하세요'}
                    </ImportClassSelectText>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </ImportClassSelectButton>
                  {isClassDropdownOpen && (
                    <>
                      <ImportClassDropdownOverlay onClick={() => setIsClassDropdownOpen(false)} />
                      <ImportClassDropdown>
                        <ImportClassDropdownOption
                          onClick={() => {
                            setSelectedSourceClassId('');
                            setIsClassDropdownOpen(false);
                            setSourceCategories([]);
                            setSelectedCategoryIds(new Set());
                          }}
                        >
                          클래스를 선택하세요
                        </ImportClassDropdownOption>
                        {availableClasses.map(cls => (
                          <ImportClassDropdownOption
                            key={cls.id}
                            onClick={() => handleSourceClassChange(cls.id)}
                          >
                            {cls.name}
                          </ImportClassDropdownOption>
                        ))}
                      </ImportClassDropdown>
                    </>
                  )}
                </ImportClassSelectContainer>
              </ImportSection>
              
              {/* 카테고리 목록 */}
              {isLoadingSourceCategories ? (
                <ImportLoadingText>카테고리를 불러오는 중...</ImportLoadingText>
              ) : selectedSourceClassId && sourceCategories.length === 0 ? (
                <ImportEmptyText>선택한 클래스에 카테고리가 없습니다.</ImportEmptyText>
              ) : selectedSourceClassId && sourceCategories.length > 0 ? (
                <ImportSection>
                  <ImportLabel>카테고리 선택</ImportLabel>
                  <ImportCategoryGrid>
                    {sourceCategories.map(cat => {
                      const isSelected = selectedCategoryIds.has(cat.id);
                      const existingName = localCategories.some(c => c.name.toLowerCase() === cat.name.toLowerCase());
                      
                      return (
                        <ImportCategoryBlock
                          key={cat.id}
                          $isSelected={isSelected}
                          $isDisabled={existingName}
                          onClick={() => !existingName && handleToggleCategorySelection(cat.id)}
                          title={existingName ? '이미 존재하는 카테고리입니다' : ''}
                        >
                          <ImportCategoryColor style={{ backgroundColor: cat.color }} />
                          <ImportCategoryName>{cat.name}</ImportCategoryName>
                          {isSelected && !existingName && (
                            <ImportCheckIcon>✓</ImportCheckIcon>
                          )}
                          {existingName && (
                            <ImportDisabledText>(중복)</ImportDisabledText>
                          )}
                        </ImportCategoryBlock>
                      );
                    })}
                  </ImportCategoryGrid>
                </ImportSection>
              ) : null}
            </ImportModalBody>
            
            <ImportModalFooter>
              <ImportModalButton onClick={handleCloseImportModal} $variant="secondary">
                닫기
              </ImportModalButton>
              <ImportModalButton
                onClick={handleApplySelectedCategories}
                $variant="primary"
                disabled={selectedCategoryIds.size === 0}
              >
                <span className="button-text-full">현재 클래스에 반영</span>
                <span className="button-text-short">클래스에 반영</span>
              </ImportModalButton>
            </ImportModalFooter>
          </ImportModalContent>
        </ImportModalOverlay>
      )}
      
      {/* 클래스 설정 동기화 모달 */}
      {showSyncModal && (
        <ImportModalOverlay onClick={handleCloseSyncModal}>
          <ImportModalContent onClick={(e) => e.stopPropagation()}>
            <ImportModalHeader>
              <ImportModalTitle>클래스 설정 동기화</ImportModalTitle>
              <ImportModalCloseButton onClick={handleCloseSyncModal}>×</ImportModalCloseButton>
            </ImportModalHeader>
            
            <ImportModalBody>
              <ImportSection>
                <ImportLabel>동기화할 클래스 선택</ImportLabel>
                <ImportClassSelectContainer>
                  <ImportClassSelectButton
                    onClick={() => setIsSyncClassDropdownOpen(!isSyncClassDropdownOpen)}
                    type="button"
                  >
                    <ImportClassSelectText>
                      {syncSelectedClassId 
                        ? syncAvailableClasses.find(c => c.id === syncSelectedClassId)?.name || '클래스를 선택하세요'
                        : '클래스를 선택하세요'}
                    </ImportClassSelectText>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </ImportClassSelectButton>
                  {isSyncClassDropdownOpen && (
                    <>
                      <ImportClassDropdownOverlay onClick={() => setIsSyncClassDropdownOpen(false)} />
                      <ImportClassDropdown>
                        <ImportClassDropdownOption
                          onClick={() => {
                            setSyncSelectedClassId('');
                            setIsSyncClassDropdownOpen(false);
                          }}
                        >
                          클래스를 선택하세요
                        </ImportClassDropdownOption>
                        {syncAvailableClasses.map(cls => (
                          <ImportClassDropdownOption
                            key={cls.id}
                            onClick={() => {
                              setSyncSelectedClassId(cls.id);
                              setIsSyncClassDropdownOpen(false);
                            }}
                          >
                            {cls.name}
                          </ImportClassDropdownOption>
                        ))}
                      </ImportClassDropdown>
                    </>
                  )}
                </ImportClassSelectContainer>
              </ImportSection>
              
              <SyncWarningMessage>
                ⚠️ 주의: 동기화하면 현재 클래스의 다음 설정들이 모두 초기화되고 선택한 클래스의 설정으로 대체됩니다.
                <br />• 기본 환경설정 (과제 카테고리, 노트 카테고리, 기본 포인트 등)
                <br />• 포인트 세트 설정
                <br />• 아이템 카드 설정
                <br />• 레벨 시스템 설정
                <br />• 타임태그 설정
                <br />
                <br />이 작업은 되돌릴 수 없습니다.
              </SyncWarningMessage>
            </ImportModalBody>
            
            <ImportModalFooter>
              <ImportModalButton onClick={handleCloseSyncModal} $variant="secondary">
                닫기
              </ImportModalButton>
              <ImportModalButton
                onClick={handleOpenSyncConfirm}
                $variant="primary"
                disabled={!syncSelectedClassId || isSyncing}
              >
                동기화하기
              </ImportModalButton>
            </ImportModalFooter>
          </ImportModalContent>
        </ImportModalOverlay>
      )}

      {/* 동기화 확인 모달 */}
      {showSyncConfirmModal && (
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmModal
            isOpen={showSyncConfirmModal}
            onClose={() => setShowSyncConfirmModal(false)}
            onConfirm={handleSyncSettings}
            title={[
              "정말로 클래스 설정을 동기화하시겠습니까?",
              "현재 클래스의 모든 설정이 초기화되고",
              "선택한 클래스의 설정으로 완전히 대체됩니다.",
              "",
              "이 작업은 되돌릴 수 없습니다."
            ]}
            confirmText="동기화"
            cancelText="취소"
            icon="!"
            iconBackground="var(--status-exempted)"
          />
        </div>
      )}

      {/* 노트 카테고리용 다른 클래스 데이터 가져오기 모달 */}
      {showNoteImportModal && (
        <ImportModalOverlay onClick={handleCloseNoteImportModal}>
          <ImportModalContent onClick={(e) => e.stopPropagation()}>
            <ImportModalHeader>
              <ImportModalTitle>다른 클래스 데이터 가져오기 (노트 카테고리)</ImportModalTitle>
              <ImportModalCloseButton onClick={handleCloseNoteImportModal}>×</ImportModalCloseButton>
            </ImportModalHeader>
            
            <ImportModalBody>
              {/* 클래스 선택 드롭다운 */}
              <ImportSection>
                <ImportLabel>클래스 선택</ImportLabel>
                <ImportClassSelectContainer>
                  <ImportClassSelectButton
                    onClick={() => setIsNoteClassDropdownOpen(!isNoteClassDropdownOpen)}
                    type="button"
                  >
                    <ImportClassSelectText>
                      {selectedSourceClassIdForNote 
                        ? availableClassesForNote.find(c => c.id === selectedSourceClassIdForNote)?.name || '클래스를 선택하세요'
                        : '클래스를 선택하세요'}
                    </ImportClassSelectText>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 9l6 6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </ImportClassSelectButton>
                  {isNoteClassDropdownOpen && (
                    <>
                      <ImportClassDropdownOverlay onClick={() => setIsNoteClassDropdownOpen(false)} />
                      <ImportClassDropdown>
                        <ImportClassDropdownOption
                          onClick={() => {
                            setSelectedSourceClassIdForNote('');
                            setIsNoteClassDropdownOpen(false);
                            setSourceNoteCategories([]);
                            setSelectedNoteCategoryIds(new Set());
                          }}
                        >
                          클래스를 선택하세요
                        </ImportClassDropdownOption>
                        {availableClassesForNote.map(cls => (
                          <ImportClassDropdownOption
                            key={cls.id}
                            onClick={() => handleSourceClassChangeForNote(cls.id)}
                          >
                            {cls.name}
                          </ImportClassDropdownOption>
                        ))}
                      </ImportClassDropdown>
                    </>
                  )}
                </ImportClassSelectContainer>
              </ImportSection>
              
              {/* 노트 카테고리 목록 */}
              {isLoadingSourceNoteCategories ? (
                <ImportLoadingText>노트 카테고리를 불러오는 중...</ImportLoadingText>
              ) : selectedSourceClassIdForNote && sourceNoteCategories.length === 0 ? (
                <ImportEmptyText>선택한 클래스에 노트 카테고리가 없습니다.</ImportEmptyText>
              ) : selectedSourceClassIdForNote && sourceNoteCategories.length > 0 ? (
                <ImportSection>
                  <ImportLabel>노트 카테고리 선택</ImportLabel>
                  {/* 현재 남은 카테고리 수 표시 */}
                  <ImportLimitInfo>
                    남은 카테고리 수: {(() => {
                      const limits = getLicenseLimits(user.userType);
                      if (limits.maxNoteCategories === null) return '무제한';
                      const remaining = limits.maxNoteCategories - localNoteCategories.length;
                      return remaining > 0 ? `${remaining}개` : '0개 (가득 참)';
                    })()}
                  </ImportLimitInfo>
                  <ImportCategoryGrid>
                    {sourceNoteCategories.map(cat => {
                      const isSelected = selectedNoteCategoryIds.has(cat.id);
                      const existingName = localNoteCategories.some(c => c.name.toLowerCase() === cat.name.toLowerCase());
                      
                      // 제한 체크: 선택 가능한지 확인
                      const canSelect = (() => {
                        if (existingName) return false;
                        const limitCheck = checkNoteCategoryLimit(user, localNoteCategories.length + selectedNoteCategoryIds.size);
                        if (!limitCheck.allowed) return false;
                        if (isSelected) return true;
                        // 새로 선택하려는 경우
                        const newLimitCheck = checkNoteCategoryLimit(user, localNoteCategories.length + selectedNoteCategoryIds.size + 1);
                        return newLimitCheck.allowed;
                      })();
                      
                      return (
                        <ImportCategoryBlock
                          key={cat.id}
                          $isSelected={isSelected}
                          $isDisabled={!canSelect}
                          onClick={() => canSelect && handleToggleNoteCategorySelection(cat.id)}
                          title={existingName ? '이미 존재하는 카테고리입니다' : !canSelect ? '선택할 수 없습니다' : ''}
                        >
                          <ImportCategoryName>{cat.name}</ImportCategoryName>
                          {isSelected && canSelect && (
                            <ImportCheckIcon>✓</ImportCheckIcon>
                          )}
                          {existingName && (
                            <ImportDisabledText>(중복)</ImportDisabledText>
                          )}
                        </ImportCategoryBlock>
                      );
                    })}
                  </ImportCategoryGrid>
                </ImportSection>
              ) : null}
            </ImportModalBody>
            
            <ImportModalFooter>
              <ImportModalButton onClick={handleCloseNoteImportModal} $variant="secondary">
                닫기
              </ImportModalButton>
              <ImportModalButton
                onClick={handleApplySelectedNoteCategories}
                $variant="primary"
                disabled={selectedNoteCategoryIds.size === 0}
              >
                <span className="button-text-full">현재 클래스에 반영</span>
                <span className="button-text-short">클래스에 반영</span>
              </ImportModalButton>
            </ImportModalFooter>
          </ImportModalContent>
        </ImportModalOverlay>
      )}
      
      {/* 토스트 메시지 */}
      {showToast && (
        <ToastOverlay>
          <ToastMessage>
            {toastMessage}
          </ToastMessage>
        </ToastOverlay>
      )}
    </>
  );
};

// 클래스 연동 설정 스타일
const SyncSettingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 0;
  border-bottom: 1px solid var(--color-border);
`;

const SyncSettingLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ToggleSwitch = styled.div<{ $isOn: boolean; $disabled?: boolean }>`
  display: flex;
  background: ${props => props.$disabled ? 'var(--color-border-strong)' : 'var(--color-accent-strong)'};
  border-radius: 13px;
  padding: 2px;
  position: relative;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  height: 26px;
  min-width: 120px;
  width: auto;
  flex-shrink: 0;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: ${props => props.$isOn ? '50%' : '2px'};
    width: calc(50% - 2px);
    height: calc(100% - 4px);
    background: var(--color-surface);
    border-radius: 11px;
    transition: left 0.3s ease;
    z-index: 1;
  }
`;

const ToggleSlider = styled.div<{ $isOn: boolean; $disabled?: boolean }>`
  flex: 1;
  padding: 2px 12px;
  font-size: 11px;
  font-weight: 600;
  color: ${props => {
    if (props.$disabled && props.$isOn) {
      return 'var(--color-text-muted)';
    }
    return props.$isOn ? 'var(--color-toggle-active)' : 'var(--color-toggle-inactive)';
  }};
  text-align: center;
  position: relative;
  z-index: 2;
  transition: color 0.3s ease;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;
  
  @media (max-width: 600px) {
    padding: 2px 10px;
    font-size: 10px;
  }
  
  @media (max-width: 480px) {
    padding: 2px 8px;
    font-size: 9px;
  }
`;

const SyncButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  margin-top: 16px;
  border: 2px solid var(--color-accent-strong);
  background: var(--color-accent-strong);
  color: white;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    border-color: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const SyncWarningMessage = styled.div`
  margin-top: 20px;
  padding: 16px;
  background: rgba(255, 193, 7, 0.1);
  border: 1px solid rgba(255, 193, 7, 0.3);
  border-radius: 8px;
  font-size: 13px;
  line-height: 1.6;
  color: var(--color-text-primary);
`;

export default AssignmentSettingsModal;

const Overlay = styled.div` position: fixed; inset: 0; background: rgba(0,0,0,0.5); z-index: 9999; `;
const Wrapper = styled.div` 
  position: fixed; 
  inset: 0; 
  z-index: 10000; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
  padding: 20px;
  pointer-events: none;
  
  @media (max-width: 768px) {
    align-items: flex-start;
    padding: 10px;
    overflow-y: auto;
  }
`;
const Content = styled.div` 
  background: var(--color-surface); 
  border-radius: 12px; 
  width: 100%; 
  max-width: 600px; 
  max-height: 88vh;
  box-shadow: var(--modal-shadow); 
  border: var(--modal-border);
  display: flex;
  flex-direction: column;
  position: relative;
  pointer-events: auto;
  
  @media (max-width: 768px) {
    margin: 0;
    max-height: 83vh;
    overflow-y: auto;
  }
`;

const HeaderSection = styled.div`
  padding: 12px 20px 12px 20px;
  flex-shrink: 0;
  border-bottom: 1px solid var(--color-border);
`;

const Title = styled.h3` 
  margin: 0; 
  text-align: center; 
  :root[data-theme='light'] & {
    color: #000;
  }
  :root:not([data-theme='light']) & {
    color: #fff;
  }
`;

const ScrollableContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 20px;
  max-height: calc(88vh - 200px);
  
  /* 스크롤바 스타일링 */
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: var(--color-panel);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: var(--color-border-strong);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
  }
  
  @media (max-width: 768px) {
    padding: 0 16px;
  }
`;

const FooterSection = styled.div`
  padding: 6px 20px 20px 20px;
  flex-shrink: 0;
  border-top: 1px solid var(--color-border);
`;

const PointsSection = styled.div`
  margin-top: 20px;
  margin-bottom: 20px;
  background: var(--color-section-bg);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--color-border);
`;

const SectionTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 1.1rem;
  color: var(--color-settings-section-title);
  font-weight: 600;
  background: var(--color-section-title-bg);
  padding: 4px 16px;
  border: 1px solid var(--color-section-title-border);
  border-radius: 20px;
  display: inline-block;
  margin-bottom: 20px;
  text-align: center;
  width: 100%;
`;

const PointsRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
`;

const PointsLabel = styled.label`
  font-weight: 600;
  color: var(--color-text-primary);
  min-width: 120px;
`;

const PointsButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
  
  @media (max-width: 420px) {
    gap: 6px;
  }
`;

const PointsButton = styled.button<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  background: var(--color-surface);
  color: ${props => props.$isSelected ? 'var(--color-text-primary)' : 'var(--color-text-muted)'};
  border: 1px solid var(--color-border);
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  flex: 1;
  min-height: 36px;
  min-width: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  font-size: 14px;
  font-weight: ${props => props.$isSelected ? '600' : '400'};
  
  &:hover {
    background-color: var(--color-panel);
    border-color: var(--color-text-muted);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    padding: 6px 8px;
    min-height: 32px;
    font-size: 12px;
  }
`;

const CategorySection = styled.div`
  margin-top: 20px;
  background: var(--color-section-bg);
  padding: 20px;
  border-radius: 12px;
  border: 1px solid var(--color-section-border);
`;

const GuestEntryCategorySection = styled(CategorySection)`
  margin-bottom: 40px;
`;

const CreateSection = styled.div`
  margin-bottom: 20px;
`;

const CreateTitle = styled.h5`
  margin: 0 0 12px 0;
  font-size: 1rem;
  color: var(--color-text-primary);
  font-weight: 600;
`;

const ListSection = styled.div`
  margin-top: 20px;
  overflow: visible;
  position: relative;
`;

const ListTitle = styled.h5`
  margin: 0 0 12px 0;
  font-size: 1rem;
  color: var(--color-text-primary);
  font-weight: 600;
`;

const Divider = styled.hr`
  border: none;
  height: 1px;
  background-color: var(--color-border);
  margin: 20px 0;
`;

const Row = styled.div` 
  display: grid; 
  grid-template-columns: 1fr 26px 26px; 
  gap: 8px; 
  align-items: center;
  
  @media (max-width: 420px) {
    grid-template-columns: 1fr 26px 26px;
    gap: 6px;
  }
`;

const NoteCategoryRow = styled.div` 
  display: grid; 
  grid-template-columns: 1fr 26px; 
  gap: 8px; 
  align-items: center;
  
  @media (max-width: 420px) {
    grid-template-columns: 1fr 26px;
    gap: 6px;
  }
`;

const List = styled.div` 
  display: flex; 
  flex-direction: column; 
  gap: 8px; 
  position: relative;
`;

const DragHandle = styled.div`
  width: 24px;
  min-width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-muted);
  cursor: grab;
  user-select: none;

  &::before {
    content: '☰';
    font-size: 18px;
    line-height: 1;
  }
`;

const Item = styled.div` 
  display: grid; 
  grid-template-columns: 1fr 26px 26px 24px; 
  gap: 8px; 
  align-items: center;
  
  @media (max-width: 420px) {
    grid-template-columns: 1fr 26px 26px 24px;
    gap: 6px;
  }
`;

const NoteCategoryItem = styled.div` 
  display: grid; 
  grid-template-columns: 1fr 26px 26px 24px; 
  gap: 8px; 
  align-items: center;
  
  @media (max-width: 420px) {
    grid-template-columns: 1fr 26px 26px 24px;
    gap: 6px;
  }
`;
const Input = styled.input` 
  padding: 8px 10px; 
  border: 1px solid var(--color-border); 
  border-radius: 6px; 
  font-size: 1rem; 
  width: 100%;
  min-width: 100px;
  box-sizing: border-box;
  background: var(--color-category-input-bg);
  color: var(--color-text-primary);
  
  &::placeholder {
    color: var(--color-text-muted);
  }
  
  &:focus-visible { 
    outline: 2px solid #000; 
    outline-offset: 2px; 
  }
  
  @media (max-width: 420px) {
    padding: 6px 6px;
    font-size: 0.9rem;
    min-width: 100px;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 24px;
`;

const Button = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: var(--color-panel);
    border-color: var(--color-text-muted);
  }

  &:active {
    transform: translateY(0);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px 24px;
  background: ${props => props.disabled ? 'var(--color-accent)' : 'var(--color-accent-strong)'};
  color: ${props => props.disabled ? 'var(--color-save-button-disabled-text)' : 'white'};
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: var(--color-accent);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const PrimaryButton = styled.button`
  padding: 0;
  background: var(--color-accent-strong);
  color: #fff;
  border: 2px solid var(--color-accent-strong);
  border-radius: 50%;
  font-weight: 600;
  cursor: pointer;
  font-size: 16px;
  width: 26px;
  height: 26px;
  min-width: 26px;
  min-height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    width: 26px;
    height: 26px;
    min-width: 26px;
    min-height: 26px;
    font-size: 14px;
  }
`;

const LockButtonTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &[data-tooltip]::after,
  &[data-tooltip]::before {
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.1s ease;
  }

  &[data-tooltip]::after {
    content: attr(data-tooltip);
    position: absolute;
    bottom: calc(100% + 6px);
    left: 50%;
    transform: translateX(-50%);
    background: #505050;
    color: #fff;
    font-size: 11px;
    font-weight: 500;
    padding: 4px 8px;
    border-radius: 6px;
    white-space: nowrap;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    z-index: 10;
  }

  &[data-tooltip]::before {
    content: '';
    position: absolute;
    bottom: calc(100% + 2px);
    left: 50%;
    transform: translateX(-50%);
    border-left: 5px solid transparent;
    border-right: 5px solid transparent;
    border-top: 5px solid #505050;
    z-index: 9;
  }

  &:hover::after,
  &:hover::before {
    opacity: 1;
  }
`;

const LockButton = styled.button<{ $locked?: boolean }>`
  width: 26px;
  height: 26px;
  min-width: 26px;
  min-height: 26px;
  border-radius: 50%;
  background: var(--color-border);
  color: var(--color-text-secondary);
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg, img {
    width: 14px;
    height: 14px;
    display: block;
  }
  
  &:hover {
    background: #d5d5d5;
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const DangerButton = styled.button`
  width: 26px;
  height: 26px;
  min-width: 26px;
  min-height: 26px;
  border-radius: 50%;
  background: var(--color-border);
  color: var(--color-text-secondary);
  border: none;
  cursor: pointer;
  transition: background 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 14px;
    height: 14px;
  }
  
  &:hover {
    background: #d5d5d5;
  }
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(73, 80, 87, 0.3);
  }
  
  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px rgba(73, 80, 87, 0.3);
  }
  
  @media (max-width: 420px) {
    width: 26px;
    height: 26px;
    min-width: 26px;
    min-height: 26px;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

// 노트북 잠금/잠금해제 비밀번호 모달 공통 스타일
const NotebookLockPasswordModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10001;
`;

const NotebookLockPasswordModalContent = styled.div`
  background: var(--color-surface);
  border: var(--modal-shell-border);
  border-radius: 12px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: var(--modal-shadow-large);
`;

const NotebookLockPasswordModalTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  color: var(--color-text-primary);
`;

const NotebookLockPasswordModalDesc = styled.p`
  margin: 0 0 16px 0;
  font-size: 14px;
  color: var(--color-text-secondary);
  line-height: 1.5;
`;

const NotebookLockPasswordModalInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 2px solid var(--color-border);
  border-radius: 8px;
  font-size: 16px;
  color: var(--color-text-primary);
  background: var(--color-surface);
  box-sizing: border-box;
  margin-bottom: 10px;
  &:focus {
    outline: none;
    border-color: var(--color-accent-strong);
  }
  &::placeholder {
    color: var(--color-text-muted);
  }
`;

const NotebookLockPasswordModalError = styled.p`
  margin: 0 0 12px 0;
  font-size: 13px;
  color: #c0392b;
`;

const NotebookLockPasswordModalButtons = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
`;

const NotebookLockPasswordModalButton = styled.button<{ $primary?: boolean }>`
  flex: 1;
  padding: 10px 20px;
  border: 2px solid var(--color-border);
  background: ${props => props.$primary ? 'var(--color-accent-strong)' : 'transparent'};
  color: ${props => props.$primary ? 'white' : 'var(--color-text-primary)'};
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: ${props => props.$primary ? '#0D4039' : 'var(--color-panel)'};
  }
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
`;

const ColorSelector = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  z-index: 1002;
`;

const ColorDisplay = styled.button<{ $color: string }>`
  width: 26px;
  height: 26px;
  min-width: 26px;
  min-height: 26px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    width: 26px;
    height: 26px;
    min-width: 26px;
    min-height: 26px;
  }
`;

const ColorPickerPopup = styled.div`
  position: fixed;
  z-index: 10000;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 8px;
  padding: 8px;
  box-shadow: var(--modal-shadow);
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 6px;
  min-width: 140px;
  
  @media (max-width: 420px) {
    padding: 6px;
    gap: 4px;
    min-width: 120px;
  }
`;

const ColorOption = styled.button<{ $color: string; $isSelected: boolean }>`
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background-color: ${props => props.$color};
  border: 2px solid ${props => props.$isSelected ? '#000' : 'transparent'};
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 8px rgba(0,0,0,0.2);
  }
  
  &:focus-visible {
    outline: 2px solid #000;
    outline-offset: 2px;
  }
  
  @media (max-width: 420px) {
    width: 20px;
    height: 20px;
  }
`;

// 과제 상태별 기본 지급 포인트 설정 스타일
const StatusPointsSection = styled.div`
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid var(--color-border);
`;

const StatusPointsTitle = styled.h4`
  margin: 0 0 16px 0;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text-dark-gray);
  text-align: center;
`;

const StatusPointsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  width: 100%;
  
  @media (max-width: 600px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 10px;
  }
  
  @media (max-width: 420px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 8px;
  }
`;

const StatusPointItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const StatusPointBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 8px 6px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  width: 100%;
  min-width: 0;
  min-height: 100px;
  height: 100%;
`;

const StatusPointHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  justify-content: center;
`;

const StatusPointColorDot = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 10px;
  color: white;
  text-shadow: 0 1px 2px rgba(0,0,0,0.3);
  flex-shrink: 0;
`;

const StatusPointLabel = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-muted);
  white-space: nowrap;
  flex-shrink: 0;
`;

const StatusPointInput = styled.input`
  width: 100%;
  max-width: 80px;
  padding: 2px 8px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  background: var(--color-surface);
  color: var(--color-text-dark-gray);
  font-size: 12px;
  text-align: center;
  transition: all 0.2s ease;
  box-sizing: border-box;
  
  /* 숫자 입력 필드 스피너(화살표) 제거 */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none !important;
    margin: 0 !important;
    display: none !important;
  }
  
  &[type=number] {
    -moz-appearance: textfield !important;
    appearance: textfield !important;
  }
  
  /* 추가 스피너 제거 */
  &::-webkit-inner-spin-button,
  &::-webkit-outer-spin-button {
    -webkit-appearance: none !important;
    appearance: none !important;
    margin: 0 !important;
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-border);
  }
  
  &::placeholder {
    color: var(--color-text-muted);
  }
  
  @media (max-width: 420px) {
    max-width: 70px;
    padding: 1px 6px;
    font-size: 11px;
  }
`;

const StatusPointText = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--color-text-muted);
  text-align: center;
  padding: 2px 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StatusPointHint = styled.div`
  font-size: 10px;
  color: var(--color-text-muted);
  text-align: center;
  line-height: 1.2;
`;

const ImportButton = styled.button`
  width: 100%;
  padding: 12px 16px;
  margin-top: 12px;
  border: 2px dashed var(--color-border);
  background: transparent;
  color: var(--color-text-muted);
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    border-color: rgba(var(--color-accent-rgb), 0.5);
    color: var(--color-accent-text);
    background: var(--color-accent-soft);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ImportModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  z-index: 10000;
  overflow-y: auto;
  padding: 60px 20px;
`;

const ImportModalContent = styled.div`
  background: var(--color-surface);
  border-radius: 12px;
  width: 90%;
  max-width: 600px;
  display: flex;
  flex-direction: column;
  box-shadow: var(--modal-shadow-large);
  border: var(--modal-border);
  margin: 0 auto;
`;

const ImportModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  border-bottom: 1px solid var(--color-border);
`;

const ImportModalTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
`;

const ImportModalCloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  
  &:hover {
    background: var(--color-border);
    color: var(--color-text-primary);
  }
`;

const ImportModalBody = styled.div`
  padding: 24px;
`;

const ImportSection = styled.div`
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ImportLabel = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  margin-bottom: 12px;
`;

const ImportClassSelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

const ImportClassSelectButton = styled.button`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid var(--color-border);
  border-radius: 6px;
  font-size: 14px;
  box-sizing: border-box;
  background: var(--color-surface);
  color: var(--color-text-primary);
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  transition: all 0.2s ease;
  
  &:hover {
    border-color: var(--color-accent-text);
  }
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-text);
    box-shadow: 0 0 0 2px rgba(var(--color-accent-rgb), 0.3);
  }
  
  svg {
    flex-shrink: 0;
    color: var(--color-text-secondary);
  }
`;

const ImportClassSelectText = styled.span`
  flex: 1;
  text-align: left;
`;

const ImportClassDropdownOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1000;
`;

const ImportClassDropdown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: 6px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  z-index: 1001;
  margin-top: 4px;
  overflow: visible;
`;

const ImportClassDropdownOption = styled.div`
  padding: 10px 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: var(--color-text-primary);
  text-align: left;
  
  &:hover {
    background-color: var(--color-panel);
  }
  
  &:not(:last-child) {
    border-bottom: 1px solid var(--color-border);
  }
`;

const ImportCategoryGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 12px;
`;

const ImportCategoryBlock = styled.div<{ $isSelected: boolean; $isDisabled: boolean }>`
  position: relative;
  width: 100%;
  padding: 8px 12px;
  border: 2px solid ${props => props.$isSelected ? 'var(--color-accent-strong)' : 'var(--color-border)'};
  border-radius: 8px;
  background: ${props => props.$isSelected ? 'rgba(16, 80, 74, 0.05)' : 'var(--color-surface)'};
  cursor: ${props => props.$isDisabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  opacity: ${props => props.$isDisabled ? 0.5 : 1};
  box-sizing: border-box;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 44px;
  
  &:hover {
    ${props => !props.$isDisabled && `
      border-color: var(--color-accent);
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    `}
  }
`;

const ImportCategoryColor = styled.div`
  width: 24px;
  height: 24px;
  min-width: 24px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  flex-shrink: 0;
`;

const ImportCategoryName = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: var(--color-text-primary);
  flex: 1;
`;

const ImportCheckIcon = styled.div`
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  background: var(--color-accent-strong);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: bold;
  flex-shrink: 0;
`;

const ImportDisabledText = styled.div`
  font-size: 11px;
  color: var(--color-text-muted);
  flex-shrink: 0;
`;

const ImportLoadingText = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-secondary);
  font-size: 14px;
`;

const ImportEmptyText = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: var(--color-text-muted);
  font-size: 14px;
`;

const ImportModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: stretch;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid var(--color-border);
`;

const ImportModalButton = styled.button<{ $variant: 'primary' | 'secondary' }>`
  flex: 1;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  .button-text-full {
    display: inline;
  }

  .button-text-short {
    display: none;
  }

  @media (max-width: 480px) {
    .button-text-full {
      display: none;
    }

    .button-text-short {
      display: inline;
    }
  }
  
  ${props => props.$variant === 'primary' ? `
    background: var(--color-accent-strong);
    color: white;
    
    &:hover:not(:disabled) {
      background: var(--color-accent);
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
    
    &:disabled {
      background: var(--color-border);
      color: var(--color-text-muted);
      cursor: not-allowed;
      opacity: 0.5;
    }
  ` : `
    background: var(--color-border);
    color: var(--color-text-primary);
    
    &:hover {
      background: var(--color-text-muted);
      color: white;
    }
  `}
  
  &:active:not(:disabled) {
    transform: translateY(0);
  }
  
  &:focus-visible {
    outline: 2px solid var(--color-accent-strong);
    outline-offset: 2px;
  }
`;

const ImportLimitInfo = styled.div`
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: 12px;
  padding: 8px 12px;
  background: var(--color-border);
  border-radius: 6px;
`;

const ToastOverlay = styled.div`
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 20000;
  pointer-events: none;
`;

const ToastMessage = styled.div`
  padding: 12px 20px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  border-radius: 8px;
  font-size: 14px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease-out;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`; 